<!-- Copyright (c) 2021 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div :class="['table-box-main', { 'fit-content-width': fitContentWidth }]" ref="mainTable">
    <div class="top-block" :style="{minHeight: topSectionHeight}">
      <div class="right-section" :style="{paddingTop: rightSectionPadding}">
        <div class="title">{{ title }}</div>
        <Tooltip
          v-if="showRefreshButton"
          @click.native="$emit('refreshClicked')"
          icon="https://dsmlib.logistics.systems/Icons/icons8-refresh-64.png"
          tooltipText="Refresh"
          style="cursor: pointer; margin-left: 5px; margin-right: 10px"
        />
        <Tooltip
          @click.native="downloadCSV"
          v-if="allowExport && privateTableData.length > 0"
          icon="https://dsmapi.logistics.systems/dsm6gIcons/ExportCSV.png"
          tooltipText="Download CSV"
          style="cursor: pointer; margin-right: 5px"
        />
        <Tooltip
          @click.native="downloadExcel"
          v-if="allowExport && privateTableData.length > 0"
          icon="https://dsmapi.logistics.systems/dsm6gIcons/ExportExcel.png"
          tooltipText="Download Excel"
          style="cursor: pointer; margin-left: 5px; margin-right: 5px"
        />
        <Tooltip
          v-if="columnOrderID !== null && privateTableData.length > 0"
          @click.native="columnOrderTooltipClicked"
          icon="https://dsmlib.logistics.systems/Icons/Columns.png"
          :tooltipText="columnOrderTooltipText"
          style="cursor: pointer; margin-left: 5px; margin-right: 5px"
        />
        <Tooltip
          v-if="isDevelopment && Object.keys(dataType).length == 0"
          icon="https://dsmlib.logistics.systems/Icons/warning.png"
          tooltipText="Data type is not set"
          style="margin-right: 5px"
        />
      </div>
      <div v-if="showSelectAll && maxSelection == -1 && pSelectedObject.length == 0" style="padding: 3px; margin-right: 6px">
        <button @click="allRowClicked">Select All</button>
      </div>
      <div v-if="pSelectedObject.length > 0" style="padding: 3px; margin-right: 6px">
        <button @click="clearSelectedClicked">Clear Selected</button>
      </div>
      <div class="left-section">
        <div v-if="lastRefreshed" class="last-refreshed">
          <img src="https://dsmlib.logistics.systems/Icons/icons8-refresh-64.png" alt="" title="Last Refreshed">
          {{lastRefreshed}}
        </div>
        <div class="selected-indicator" v-if="pSelectedObject.length > 0">
          <span>Selected {{ pSelectedObject.length }} of {{ showData.length }}</span>
          <div
            class="clear-arrow"
            @click="viewSelectedSection = !viewSelectedSection"
            :style="{ transform: `scaleY(${viewSelectedSection ? -1 : 1})` }"
          ></div>
          <div v-if="viewSelectedSection" class="selected-button-block">
            <button @click="showSelectedClicked">{{ showSelectedOnly ? 'Show All' : 'Show Selected Only' }}</button>
          </div>
          <!-- <button @click="clearSelectedClicked">Clear</button> -->
        </div>
        <div v-if="apiEndpoint && apiPayload"
          >{{ ssrRowCount }} of {{ ssrTotalRows }} records
          {{ privateTableData.length > 0 ? `(Page ${currentPage} of ${totalPages})` : '' }}</div
        >
        <div v-else
          >{{ showData.length }} of
          {{ hasDataType() ? privateTableData.length - 1 : privateTableData.length }} records
          {{ currentRowCount == -1 ? '' : `(Page ${currentPage} of ${totalPages})` }}</div
        >
      </div>
    </div>
    <div
      v-if="(showData.length > 0 || columnSearch.length > 0) && privateTableData.length > 0"
      class="table-block"
      @scroll="mainScroll"
      ref="scrollBlock"
    >
      <div :class="['table-header', `${theme}-theme`]" ref="headerBlock">
        <div
          class="table-column"
          :columnname="k"
          :class="[`${theme}-theme`]"
          :style="{
            position: stickColumn.includes(i) ? 'sticky' : 'relative',
            left: stickColumn.includes(i) ? '0' : 'relative',
            display: hideColumns.includes(k) || appliedColumnOrderViewHiddenColumns.includes(k) ? 'none' : 'relative',
            zIndex: stickColumn.includes(i) ? 10 : '',
          }"
          v-for="(k, i) in columnArrange"
          :key="i"
        >
          <div
            class="title-block"
            @mousedown="headerMouseDown"
            v-if="k && !adjustedAdditionalButtons.map((b) => b['index']).includes(i)"
          >
            <span>{{ k }}</span>
            <img
              :style="{
                opacity: k == currentSortColumn ? '1' : '0.2',
                transform: `rotate(${currentSortDirection == 1 && k == currentSortColumn ? '180' : '0'}deg)`,
              }"
              src="https://dsmlib.logistics.systems/Icons/SortWhite.png"
              height="10"
            />
          </div>
          <input
            v-if="!hideSearch && k && !adjustedAdditionalButtons.map((b) => b['index']).includes(i)"
            @input="inputChanged($event, i)"
            :type="
              tableDataType[k] ?
                tableDataType[k] == 'currency' || tableDataType[k] == 'rate' ? 'number'
                : tableDataType[k] == 'datetime' ? 'date'
                : tableDataType[k]
              : 'text'
            "
            searchLimit="lower"
            :placeholder="getFirstFilterInputPlaceholder(tableDataType[k])"
          /><br v-if="!hideSearch" />
          <input
            v-if="
              !hideSearch && k &&
              (tableDataType[k] == 'number' ||
                tableDataType[k] == 'date' ||
                tableDataType[k] == 'currency' ||
                tableDataType[k] == 'rate' ||
                tableDataType[k] == 'datetime')
            "
            @input="inputChanged($event, i)"
            :type="
              tableDataType[k] ?
                tableDataType[k] == 'currency' || tableDataType[k] == 'rate' ? 'number'
                : tableDataType[k] == 'datetime' ? 'date'
                : tableDataType[k]
              : 'text'
            "
            searchLimit="upper"
            :placeholder="getSecondFilterInputPlaceholder(tableDataType[k])"
          />

          <div class="resizer" @mousedown="resizeMouseDown($event, k)"></div>
        </div>
      </div>

      <div
        v-if="isHeaderMouseDown"
        class="dragging-container"
        @mouseup="headerMouseUp"
        @mousemove="headerMouseMove"
        ref="draggingContainer"
        :style="{ top: `${$refs['scrollBlock'].scrollTop}px`, left: `${$refs['scrollBlock'].scrollLeft}px` }"
      ></div>
      <div
        v-if="isResizing"
        class="resizing-container"
        @mouseup="resizeMouseUp"
        @mousemove="resizeMouseMove"
        ref="resizingContainer"
        :style="{ top: `${$refs['scrollBlock'].scrollTop}px`, left: `${$refs['scrollBlock'].scrollLeft}px` }"
      ></div>
      <!-- <div :id="`dataBlock${tableID}`" class="data-block" :class="`${theme}-theme-child`"></div> -->
    </div>
    <div
      v-if="(showData.length > 0 || columnSearch.length > 0) && privateTableData.length > 0"
      :class="[`bottom-block`, { 'd-none': hideBottomBlock }, `${theme}-theme`]"
    >
      <div class="bottom-content" :style="{ zIndex: widerTable ? '99' : '-99' }">
        <div class="buttons-content">
          <div style="white-space: nowrap">Rows per page:</div>
          <button v-if="groupBy == ''" :class="{ 'active-blue-theme': currentRowCount == 10 }" @click="changeRows"
            >10</button
          >
          <button v-if="groupBy == ''" :class="{ 'active-blue-theme': currentRowCount == 20 }" @click="changeRows"
            >20</button
          >
          <button v-if="groupBy == ''" :class="{ 'active-blue-theme': currentRowCount == 30 }" @click="changeRows"
            >30</button
          >
          <button v-if="groupBy == ''" :class="{ 'active-blue-theme': currentRowCount == 50 }" @click="changeRows"
            >50</button
          >
          <button
            v-if="!apiEndpoint && !apiPayload"
            :class="{ 'active-blue-theme': currentRowCount == -1 }"
            :disabled="groupBy != ''"
            @click="changeRows"
            >All</button
          >
        </div>
        <div class="bottom-spacer" ref="bottomSpacer"></div>
        <div class="buttons-content">
          <button v-if="groupBy == ''" @click="pageClicked('first')">First</button>
          <button v-if="groupBy == ''" @click="pageClicked('prev')">Prev</button>
          <div v-if="groupBy == ''" style="display: flex">
            <button
              v-for="(p, i) in buttonPages"
              :key="i"
              @click="pageClicked(p)"
              :class="[p == currentPage ? `active-${theme}-theme` : '']"
              >{{ p }}</button
            >
          </div>
          <button v-if="groupBy == ''" @click="pageClicked('next')" style="margin-left: 5px">Next</button>
          <button v-if="groupBy == ''" @click="pageClicked('last')">Last</button>
        </div>
      </div>
      <div class="bottom-content" :style="{ zIndex: !widerTable ? '99' : '-99' }">
        <div class="buttons-content">
          <button style="margin-left: 0px" @click="changeRowsV2">{{
            currentRowCount == -1 ? 'All' : currentRowCount
          }}</button>
        </div>
        <div class="bottom-spacer" ref="bottomSpacer2"></div>
        <div class="buttons-content">
          <button @click="pageClicked('prev')">&lt;</button>
          <button>{{ currentPage }}</button>
          <button @click="pageClicked('next')">&gt;</button>
        </div>
      </div>
    </div>
    <div v-if="loading" class="no-items">Loading {{ title }} data . . .</div>
    <div v-else-if="showData.length == 0 && columnSearch.length == 0" class="no-items">{{ emptyMessage }}</div>

    <!-- <div v-if="showContextMenu" class="context-menu-content" :style="contextMenuPosition">
      <div class="context-menu-items">Download</div>
    </div> -->
    <div class="context-menu-popup" v-if="showContextMenu">
      <div
        class="context-menu-background"
        @click="showContextMenu = false"
        @contextmenu="
          $event.preventDefault();
          showContextMenu = false;
        "
      ></div>
      <div class="context-menu-content" :style="contextMenuPosition">
        <div
          v-for="(cm, cmInd) in contextMenuList"
          :key="cmInd"
          class="context-menu-items"
          @click="contextMenuClicked(cm)"
          >{{ cm }}</div
        >
        <div v-if="contextMenuList.length == 0" class="context-menu-items" @click="downloadAttachmentClicked"
          >Download</div
        >
      </div>
    </div>

    <div v-if="ssrLoading" class="ssr-loading">
      <div>Loading Rows</div>
    </div>

    <div v-if="showColumnOrderPopup" class="column-order-popup">
      <div v-if="showColumnOrderViews" class="popup-content">
        <div class="call-to-action-buttons">
          <button @click="columnOrderViewsNewViewClicked">
            <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png" alt="New View" />
            New View
          </button>
          <button @click="columnOrderViewsEditViewClicked">
            <img src="https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png" alt="Edit View" />
            Edit View
          </button>
          <button @click="columnOrderViewsDeleteViewClicked">
            <img src="https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png" alt="Delete View" />
            Delete View
          </button>
        </div>
        <div class="main-content">
          <TableBox
            :tableData="columnOrderViews"
            :selectedRow.sync="columnOrderPopupViewsTableBoxSelectedRow"
            title="Views"
            :hideColumns="columnOrderPopupViewsTableBoxHideColumns"
          />
          <div class="call-to-action-buttons">
            <button @click="columnOrderViewsApplyClicked">
              <img src="https://dsmlib.logistics.systems/Icons/Save.png" alt="Apply" />
              Apply
            </button>
            <button @click="columnOrderViewsCloseClicked">
              <img src="https://dsmlib.logistics.systems/Icons/Cancel.png" alt="Close" />
              Close
            </button>
          </div>
        </div>
      </div>
      <div v-if="showColumnReorderingMenu" class="popup-content">
        <div class="call-to-action-buttons">
          <button @click="columnReorderingMenuNewColumnClicked">
            <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png" alt="New Column" />
            New Column
          </button>
          <button @click="columnReorderingMenuMoveUpClicked">
            <img src="https://dsmlib.logistics.systems/Icons/icons8-up-arrow-26.png" alt="Move Up" />
            Move Up
          </button>
          <button @click="columnReorderingMenuMoveDownClicked">
            <img src="https://dsmlib.logistics.systems/Icons/icons8-down-arrow-26.png" alt="Move Down" />
            Move Down
          </button>
        </div>
        <div class="main-content">
          <div id="view-details">
            <div id="view-general-details">
              <Input
                id="view-name-input"
                :inputValue.sync="columnReorderingMenuViewNameInputValue"
                placeholder="View Name"
                :isRequired="true"
                :errorMessage.sync="columnReorderingMenuViewNameErrorMessage"
                validator="text,200,,alphanumeric,space"
              />
              <CheckBoxV2
                id="user-owned-check-box"
                title="User Owned"
                :isChecked.sync="columnReorderingMenuUserOwnedIsChecked"
              />
              <CheckBoxV2
                id="default-check-box"
                title="Default"
                :isChecked.sync="columnReorderingMenuDefaultIsChecked"
              />
            </div>
            <span v-if="columnReorderingMenuColumnOrder.length === 0"
              >Click on <strong>New Column</strong> to add columns to this view</span
            >
            <div
              v-for="column in columnReorderingMenuColumnOrder"
              :key="column"
              :class="getColumnReorderingMenuColumnCardClasses(column)"
              @click="columnReorderingMenuColumnClicked(column)"
            >
              <span>{{ column }}</span>
              <img
                class="delete-column-icon"
                src="https://dsmlib.logistics.systems/Icons/Delete.png"
                alt="Delete Column"
                title="Delete Column"
                @click="columnReorderingMenuDeleteColumnClicked($event, column)"
              />
            </div>
          </div>
          <div class="call-to-action-buttons">
            <button @click="columnReorderingMenuSaveClicked">
              <img src="https://dsmlib.logistics.systems/Icons/Save.png" alt="Save" />
              Save
            </button>
            <button @click="columnReorderingMenuCancelClicked">
              <img src="https://dsmlib.logistics.systems/Icons/Cancel.png" alt="Cancel" />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <PopupV2
      :show="showNewColumnPopup"
      title="New Column"
      :buttons="newColumnPopupButtons"
      class="new-column-popup"
      @buttonClicked="newColumnPopupButtonClicked"
    >
      <span>Select a column to add</span>
      <Dropdown
        :items="newColumnPopupColumns"
        itemKey="Column Name"
        placeholder="Column Name"
        :selectedObject.sync="newColumnPopupSelectedColumn"
        :isRequired="true"
        :errorMessage.sync="newColumnPopupErrorMessage"
      />
    </PopupV2>
    <Loader :isVisible="loaderIsVisible" :loadingText="loaderText" :isFullScreen="true" />
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  data: function () {
    return {
      isDevelopment: false,
      loginDetails: null,

      colWidth: [],

      privateTableData: [],

      tableDataType: {},
      // tableData: [],
      showData: [],
      columnName: [],
      columnArrange: [],
      /**
       * Search column array of object
       * @example { 'column': 'column name', 'search': 'search value' }
       */
      columnSearch: [],
      currentSortColumn: '',
      currentSortDirection: -1,

      rowEnd: 50,
      rowStart: 0,

      leftScroll: 0,

      timer: null,
      currentRowCount: -1,
      totalPages: 1,
      currentPage: 1,

      viewSelectedSection: false,
      showSelectedOnly: false,

      pSelectedObject: [],

      scrollBlock: null,
      headerBlock: null,

      draggingElement: null,
      resizeElement: null,
      resizeDraggingX: null,
      resizeColumnElement: null,
      resizeIndex: null,
      oriColumnWidth: null,
      newColumnWidth: null,

      mouseDownTime: null,
      headerElement: null,
      startHeaderIndex: null,
      headerDraggingName: null,
      placeholderElement: null,
      draggingX: null,

      tagToRemove: ['img'],

      excelColumnLetters: [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
      ],

      observer: null,
      widerTable: true,

      isHeaderMouseDown: false,
      isResizing: false,
      resizeObject: null,
      resizeClickTime: null,

      showContextMenu: false,
      contextMenuPosition: { left: 0, top: 0 },
      contextMenuItem: null,
      contextMenuList: [],

      //#region Server Side Rendering
      ssrLoading: false,
      ssrRowCount: 0,
      ssrTotalRows: 0,
      ssrTotalPages: 0,
      //#endregion

      showColumnOrderViews: false,
      columnOrderPopupViewsTableBoxSelectedRow: [],
      columnOrderPopupViewsTableBoxHideColumns: ['Columns'],

      showColumnReorderingMenu: false,
      columnReorderingMenuAction: '', // [N]ew or [E]dit only, deleting views doesn't involve the column reordering menu
      columnReorderingMenuViewNameInputValue: '',
      columnReorderingMenuViewNameErrorMessage: '',
      columnReorderingMenuUserOwnedIsChecked: false,
      columnReorderingMenuDefaultIsChecked: false,
      columnReorderingMenuColumnOrder: [],
      columnReorderingMenuSelectedColumn: '', // impossible for the API to return two columns with the same name, column name alone is enough to guarantee uniqueness
      showNewColumnPopup: false,
      newColumnPopupColumns: [],
      newColumnPopupSelectedColumn: null,
      newColumnPopupErrorMessage: '',
      newColumnPopupButtons: [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }],

      // reason for not using $fullscreenLoading:
      // $fullscreenLoading controls the global loader, which means as a component, it will affect the loading overlay of the parent page
      // which can lead to unwanted behaviours
      loaderIsVisible: false,
      loaderText: '',
    };
  },
  computed: {
    buttonPages: function () {
      if (this.apiEndpoint && this.apiPayload) {
        this.totalPages = this.ssrTotalPages;
      } else {
        this.totalPages = 0;
        if (this.currentRowCount == -1) {
          this.totalPages = 1;
        } else {
          this.totalPages = Math.ceil(this.showData.length / this.currentRowCount);
        }
      }

      if (this.totalPages > 5) {
        if (this.currentPage == 1 || this.currentPage == 2) {
          return Array.from({ length: 5 }, (_, i) => i + 1);
        } else if (this.currentPage >= this.totalPages - 2) {
          return Array.from({ length: 5 }, (_, i) => i + (this.totalPages - 4));
        } else {
          return Array.from({ length: 5 }, (_, i) => i + (this.currentPage - 2));
        }
      } else {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }
    },
    appliedColumnOrderView: function () {
      if (!Array.isArray(this.columnOrderViews) || this.columnOrderViews.length === 0) {
        return null;
      }

      return this.columnOrderViews.find((view) => view.Applied === 'Yes') ?? null;
    },
    appliedColumnOrderViewColumns: function () {
      return this.appliedColumnOrderView?.Columns ?? null;
    },
    appliedColumnOrderViewHiddenColumns: function () {
      if (!this.appliedColumnOrderViewColumns) {
        return [];
      }

      return this.appliedColumnOrderViewColumns
        .filter(({ Show }) => Show === '0')
        .map(({ 'Column Name': columnName }) => columnName);
    },
    adjustedAdditionalButtons: function () {
      if (this.additionalButtons.length === 0) {
        return [];
      }

      // additional buttons passed in will specify the column index to add them to
      // if column order views aren't implemented, buttons can be added to any column
      if (this.allToggleableColumnsInTableData.length === 0 || this.appliedColumnOrderViewHiddenColumns.length === 0) {
        return this.additionalButtons;
      }

      // otherwise, buttons can only be added either to the frontmost or rearmost columns
      // if some columns are hidden by the column order, the total number of columns will change, which means the button indexes need to adapt as well
      // buttons meant to be added to the rearmost columns need to be brought forward
      // buttons cannot be added anywhere in the middle, because it's impossible to tell which buttons to bring forward and by how much when a column order view is applied
      // when adding buttons during development, make sure that no view is applied when specifying the button index
      const sortedAdditionalButtons = this.additionalButtons.sort(
        (firstButton, secondButton) => firstButton.index - secondButton.index,
      );
      const { frontButtons, rearButtons } = sortedAdditionalButtons.reduce(
        (groupedButtons, currentButton, currentIndex, buttonsArray) => {
          if (groupedButtons.rearButtons.length > 0) {
            groupedButtons.rearButtons.push(currentButton);
          } else if (currentButton.index === 0) {
            groupedButtons.frontButtons.push(currentButton);
          } else if (currentIndex === 0) {
            groupedButtons.rearButtons.push(currentButton);
          } else if (currentButton.index - 1 === buttonsArray[currentIndex - 1].index) {
            groupedButtons.frontButtons.push(currentButton);
          } else {
            groupedButtons.rearButtons.push(currentButton);
          }

          return groupedButtons;
        },
        { frontButtons: [], rearButtons: [] },
      );
      const shownColumns = this.allToggleableColumnsInTableData.filter(
        (toggleableColumn) =>
          !this.appliedColumnOrderViewHiddenColumns.some((hiddenColumn) =>
            this.caseInsensitiveStringComparison(toggleableColumn, hiddenColumn),
          ),
      );
      const lastColumnIndex = frontButtons.length + shownColumns.length;

      return frontButtons.concat(
        rearButtons.map((button, index) => ({
          ...button,
          index: lastColumnIndex + index,
        })),
      );
    },
    columnOrderTooltipText: function () {
      let tooltipText = 'Column Order';
      if (this.appliedColumnOrderView) {
        tooltipText += `\nCurrently Applied: ${this.appliedColumnOrderView['View Name']}`;
      }
      return tooltipText;
    },
    showColumnOrderPopup: function () {
      return this.showColumnOrderViews || this.showColumnReorderingMenu;
    },
    allToggleableColumnsInTableData: function () {
      if (this.tableData.length === 0) {
        return [];
      }

      const allColumns = Object.keys(this.tableData[0]); // assume columns are the same for all rows, and just take from the first row
      const toggleableColumns = allColumns.filter(
        (columnName) =>
          columnName !== '_rowid' &&
          !this.hideColumns.some((hiddenColumn) => this.caseInsensitiveStringComparison(columnName, hiddenColumn)),
      ); // columns hardcoded as hidden should never be shown to the user, the visibility of these columns are not toggleable
      return toggleableColumns;
    },

    topSectionHeight(){
      if(this.lastRefreshed)return '52px';
      return '40px';
    },

    rightSectionPadding(){
      if(this.lastRefreshed) return '8px';
      return '0px';
    }
  },
  props: {
    tableData: {
      type: Array,
      default: function () {
        return [];
      },
    }, // The data of the table
    selectedRow: {
      type: Array,
      default: function () {
        return [];
      },
    }, // The selected row of the table (Use sync for 2 way binding)
    theme: { type: String, default: 'blue' }, // Set the theme for the table (red, green and blue only)
    maxSelection: { type: Number, default: 1 }, // Set the max selection of the row (0 - cannot select, -1 - no limit, n - 0 - n selection)
    rollSelection: { type: Boolean, default: true }, // Set the false to disable user to select if the max selection reached
    title: { type: String, default: 'Title' }, // Set the title of the table
    showRefreshButton: { type: Boolean, default: false }, // Show a refresh button and emit refreshClicked when pressed
    downloadName: { type: String, default: '' }, // Set the title of the download text
    allowExport: { type: Boolean, default: true }, // Allow CSV and Excel exports
    stickColumn: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Experimental: Set the column index that you would like to stick to the left when horizontal scrolling
    columnWidth: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Set the column width for the columns, leave empty for default
    fitContentWidth: { type: Boolean, default: false }, // Size columns from their content while the table fills its parent
    loading: { type: Boolean, default: false }, // Show that the table is loading
    showSelectAll: { type: Boolean, default: false }, // Show Select All button
    additionalButtons: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Sample: { text: 'Select', index: 0, width: 75 }
    // can only add buttons to the frontmost and rearmost columns when using with columnOrderViews and columnOrderID

    selectedIndex: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Manually select the index of the row that you desired
    hideColumns: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Hide the columns name that you don't wish to see
    columnOrderViews: {
      type: Array,
      default: function () {
        return [];
      },
    }, // For column reordering. MUST USE .sync! Call GetColumnOrder from TableBoxController and pass the results as-is into this prop
    columnOrderID: {
      type: Object,
      default: function () {
        return null;
      },
    }, // For column reordering. Sample: { formname: [ModuleName]_[PageName], formctrlname: [UniqueNameForThisTableBox] }
    groupBy: { type: String, default: '' }, // Group by column name
    showSelectedCheckBox: { type: Boolean, default: false }, // Show the checkbox when selected
    emptyMessage: { type: String, default: 'No items' }, // Set the empty message of the table box when there are no available rows
    rowHeight: { type: Number, default: 0 }, // Set the height of the row, 0 means unset
    tableFilters: {
      type: Object,
      default: function () {
        return {};
      },
    }, // Set the table filters to show in the downloaded excel

    hideRow: {
      type: Object,
      default: function () {
        return {};
      },
    }, // The array of items that you wish to hide (BETA)

    additionalIconsIndex: {
      type: Array,
      default: function () {
        return [];
      },
    }, // The list of index where the column is for icons
    // does not work with columnOrderViews and columnOrderID, pass in HTML img tags in tableData as a workaround

    dataType: {
      type: Object,
      default: function () {
        return {};
      },
    }, // The data type for each column (string, number, currency, rate, date, datetime)

    allowEmptyNumeric: { type: Boolean, default: false },

    attachmentKey: { type: Object, default: null }, // Must have 3 keys; base64, name, type

    apiEndpoint: { type: String, default: '' }, // Pass the endpoint if the data is rendered from the backend
    apiPayload: { type: Object, default: null }, // Pass the endpoint if the data is rendered from the backend

    hideBottomBlock: { type: Boolean, default: false },
    hideSearch: { type: Boolean, default: false },

    lastRefreshed: null,
  },
  methods: {
    initTable: function () {
      let noRowid = this.privateTableData.map((t) => t['_rowid']).filter((t) => !t);
      if (noRowid.length > 0) {
        this.privateTableData.forEach((s, i) => {
          s['_rowid'] = `row${i}`;
        });
        this.$emit('update:tableData', this.privateTableData); // sometimes the parent page needs the _rowid too, like when programatically selecting or deselecting certain rows, and tablebox relies on the rowid to determine which rows are selected and updating the styling accordingly
      }
      this.columnName = Object.keys(this.privateTableData[0]).filter((k) => k != '_rowid');
      this.columnArrange = Object.keys(this.privateTableData[0]).filter((k) => k != '_rowid');
      this.columnArrange = this.columnArrange.filter(
        (ca) => !this.hideColumns.includes(ca) && !this.appliedColumnOrderViewHiddenColumns.includes(ca),
      );

      if (this.adjustedAdditionalButtons.length > 0) {
        this.adjustedAdditionalButtons.forEach((b) => {
          this.columnName.splice(b['index'], 0, b['key'] || 'table_box_autobutton');
          this.columnArrange.splice(b['index'], 0, b['key'] || 'table_box_autobutton');
        });
      }

      if (this.showSelectedCheckBox) {
        // Add column for the checkbox
        this.columnName.splice(0, 0, '');
        this.columnArrange.splice(0, 0, '');
      }

      this.colWidth = [];
      if (this.columnWidth.length >= 1) {
        // for (let i = 0; i < this.columnArrange.length; i++) {
        //   this.colWidth.push(this.columnWidth[0]);
        // }

        // instead of repeating the same width for all columns, allow specification of different widths for each column
        this.colWidth = this.columnWidth.slice(0, this.columnArrange.length);
      } else {
        this.columnArrange.forEach((c, i) => {
          // New version down
          var addButton = this.adjustedAdditionalButtons.filter((b) => b['index'] == i);
          let addIcon = this.additionalIconsIndex.includes(i);
          if (addButton.length > 0) {
            this.colWidth.push(addButton[0]['width'] ? addButton[0]['width'] : 50);
          } else if (addIcon || (this.showSelectedCheckBox && i == 0)) {
            // If the column is for icon OR first column and show selectedcheckbox is set to true
            this.colWidth.push(30);
          } else {
            let maxColumnWidth = this.fitContentWidth ? null : this.$refs.mainTable.getBoundingClientRect().width * 0.2;
            let columnKeyLength = c.toString().length + 4;
            let maxLengthValue = this.privateTableData.reduce((currentMax, currentRow) => {
              if (currentRow[c]) {
                if (typeof currentRow[c] == 'string') {
                  const currentCellLength = this.replaceAllTag(currentRow[c], true).length + 4;
                  return currentCellLength > currentMax ? currentCellLength : currentMax;
                } else {
                  const currentCellLength = this.replaceAllTag(currentRow[c].toString(), true).length + 4;
                  return currentCellLength > currentMax ? currentCellLength : currentMax;
                }
              } else {
                return currentMax;
              }
            }, 0);

            let maxWidth = Math.max(columnKeyLength, maxLengthValue) * 7.205 + 11;
            this.colWidth.push(
              maxWidth < 50 ? 50
              : maxColumnWidth && maxWidth > maxColumnWidth ? maxColumnWidth
              : maxWidth,
            );
          }
        });
      }

      this.showData = this.privateTableData.slice();
      if (this.hasDataType()) {
        let dataType = this.privateTableData.find((t) => t['#'] == 0);
        this.tableDataType = dataType;
        this.showData = this.showData.slice(1);
      } else {
        this.tableDataType = this.dataType || {};
      }
      this.placeholderElement = document.createElement('div');
      this.tableDataType = Object.assign(this.tableDataType, { ...this.dataType });

      this.$nextTick(() => {
        this.populateData(false, true);
      });
    },
    updateTableRender: function (hasData) {
      if (hasData) {
        this.initTable();
        this.$nextTick(() => {
          if (!this.observer) {
            this.observer = new ResizeObserver(this.sizeChanged);
          }

          this.observer.observe(this.$refs['bottomSpacer']);
          this.observer.observe(this.$refs['bottomSpacer2']);
        });
      } else {
        if (!this.scrollBlock) {
          return;
        }

        while (this.scrollBlock.children.length > 1) {
          this.scrollBlock.removeChild(this.scrollBlock.lastChild);
        }
        this.showData = [];
        this.rowEnd = 50;
        this.rowStart = 0;
        this.currentRowCount = -1;
      }
    },
    hasDataType: function () {
      if (this.privateTableData.length == 0) {
        return false;
      }
      if (Object.keys(this.privateTableData[0]).includes('#')) {
        return this.privateTableData.map((t) => (t['#'] == undefined ? '' : t['#'].toString())).includes('0');
      } else {
        return false;
      }
    },
    getFirstFilterInputPlaceholder: function (dataType) {
      switch (dataType) {
        case 'number':
        case 'currency':
        case 'rate':
          return 'From';

        case 'string':
        default:
          return 'Search';
      }
    },
    getSecondFilterInputPlaceholder: function (dataType) {
      switch (dataType) {
        case 'number':
        case 'currency':
        case 'rate':
          return 'To';

        case 'string':
        default:
          return 'Search';
      }
    },
    sortTable: function () {
      let columns = this.getColumnDetails();
      let colName = columns['columns'][this.startHeaderIndex];

      // Sort -1 = no sort | 0 = asc | 1 = desc
      if (this.currentSortColumn == colName) {
        if (this.currentSortDirection == 0) {
          this.currentSortDirection = 1;
        } else {
          this.currentSortDirection = 0;
        }
      } else {
        this.currentSortColumn = colName;
        this.currentSortDirection = 0;
      }

      let isNumber = this.tableDataType[colName] == 'number';
      if (this.currentSortDirection == 0) {
        this.showData = this.showData.sort((a, b) => {
          if (isNumber) {
            return this.stringToNumber(a[colName]) > this.stringToNumber(b[colName]) ? 1 : -1;
          } else {
            return a[colName] > b[colName] ? 1 : -1;
          }
        });
      } else {
        this.showData = this.showData.sort((a, b) => {
          if (isNumber) {
            return this.stringToNumber(b[colName]) > this.stringToNumber(a[colName]) ? 1 : -1;
          } else {
            return b[colName] > a[colName] ? 1 : -1;
          }
        });
      }

      this.$nextTick(() => this.populateData(true));
    },
    stringToNumber: function (value) {
      if (typeof value == 'number') {
        return value;
      } else if (typeof value == 'object' || typeof value == 'undefined') {
        value = '';
      }
      value = value.split(/\D/g);
      let valueSplit = value.slice(0, value.length - 1);
      let finalValue = Number(
        valueSplit.length > 0 ? `${valueSplit.join('')}.${value[value.length - 1]}` : Number(value),
      );
      return isNaN(finalValue) ? 0 : finalValue;
    },
    populateData: function (clear, firstTime) {
      this.headerBlock = this.$refs.headerBlock;
      this.scrollBlock = this.$refs.scrollBlock;
      let scrollX = this.scrollBlock.scrollLeft;

      if (clear) {
        while (this.scrollBlock.children.length > 1) {
          this.scrollBlock.removeChild(this.scrollBlock.lastChild);
        }

        if (this.apiEndpoint && this.apiPayload) {
          this.rowStart = 0;
          this.rowEnd = this.currentRowCount;
        } else {
          if (this.currentRowCount == -1) {
            this.rowStart = 0;
            this.rowEnd = 50;
          } else {
            this.rowStart = (this.currentPage - 1) * this.currentRowCount;
            this.rowEnd = this.rowStart + this.currentRowCount;
          }
        }
      }

      if (this.groupBy) {
        let grouping = [...new Set(this.showData.map((s) => s[this.groupBy]))];
        grouping = grouping.sort();

        if (this.scrollBlock.children.length - 1 >= this.showData.length + grouping.length) {
          return;
        }

        grouping.forEach((g) => {
          let groupItems = this.showData.filter((s) => s[this.groupBy] == g);
          this.scrollBlock.appendChild(this.createGroupingHead(`${g} (${groupItems.length} items)`, groupItems.length));

          groupItems.forEach((g) => {
            let index = this.pSelectedObject.findIndex(
              (s) => (s['_rowid'] && s['_rowid'] == g['_rowid']) || JSON.stringify(s) == JSON.stringify(g),
            );
            var rowDiv = document.createElement('div');
            rowDiv.classList.add('table-row', `${this.theme}-theme-row`);
            rowDiv.setAttribute('id', `${g['_rowid']}`);
            if (index >= 0) {
              rowDiv.classList.add(`${this.theme}-theme-row-selected`);
            }
            rowDiv.addEventListener('click', (e) => {
              this.rowClicked(e, e.currentTarget, g);
            });

            rowDiv.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              if (this.attachmentKey) {
                let mainTable = this.$refs['mainTable'].getBoundingClientRect();
                this.showContextMenu = true;
                let left = e.x - mainTable.left;
                if (e.x + 200 > mainTable.left + mainTable.width) {
                  left -= 200;
                }
                this.contextMenuPosition['left'] = `${left}px`;
                this.contextMenuPosition['top'] = `${e.y - mainTable.top}px`;
                this.contextMenuItem = this.showData[i];
              }
            });

            for (let [ci, c] of this.getColumnDetails()['columns'].entries()) {
              if (this.hideColumns.includes(c) || this.appliedColumnOrderViewHiddenColumns.includes(c)) {
                continue;
              }
              var colDiv = document.createElement('div');
              colDiv.classList = ['row-data'];
              if (this.rowHeight > 0) {
                colDiv.style.maxHeight = `${this.rowHeight}px`;
                colDiv.style.minHeight = `${this.rowHeight}px`;
              }
              if (Object.keys(this.tableDataType).includes(c)) {
                if (this.tableDataType[c] == 'number' || this.tableDataType[c] == 'currency' || this.tableDataType[c] == 'rate') {
                  colDiv.style.textAlign = 'right';
                  colDiv.style.justifyContent = 'flex-end';
                }
              }
              if (this.stickColumn.includes(ci)) {
                colDiv.style.position = 'sticky';
                colDiv.style.left = '0';

                if (i % 2 == 0) {
                  switch (this.theme) {
                    case 'red':
                      colDiv.style.backgroundColor = 'rgb(255, 235, 235)';
                      break;
                    case 'blue':
                      colDiv.style.backgroundColor = 'rgb(235, 235, 255)';
                      break;
                    case 'green':
                      colDiv.style.backgroundColor = '235, 255, 235';
                      break;
                    default:
                      colDiv.style.backgroundColor = 'white';
                      break;
                  }
                } else {
                  colDiv.style.backgroundColor = 'white';
                }
              }

              try {
                if (g[c] != undefined) {
                  let addIcon = this.additionalIconsIndex.includes(ci);

                  if (addIcon) {
                    let key = Object.keys(g)[ci];
                    let icon = g[key];

                    if (icon) {
                      colDiv.appendChild(this.createIcon(icon, 20));
                    }
                  } else {
                    if (this.tableDataType[c] == 'currency' || this.tableDataType[c] == 'rate' || this.tableDataType[c] == 'number') {
                      let value = this.getNumberValue(g[c]);
                      colDiv.innerHTML = this.allowEmptyNumeric && (g[c] == null || g[c] === '') ? '' : this.tableDataType[c] == 'currency' ? this.$currencyFormat(value) : this.tableDataType[c] == 'rate' ? this.$currencyFormat(value, 4) : value;
                    } else if (this.tableDataType[c] == 'date' || this.tableDataType[c] == 'datetime') {
                      let allTags = this.getAllTags(g[c]);
                      let value = '';
                      if (g[c]) {
                        value = `${new Date(this.replaceAllTag(g[c])).formatString(this.tableDataType[c] == 'date' ? this.$store.state['preferredDateFormat'] : `${this.$store.state['preferredDateFormat']} HH:mm`) || ''} ${allTags.flat().join(' ')}`;
                      }
                      colDiv.innerHTML = value;
                    } else {
                      colDiv.innerHTML = g[c];
                    }
                    if (this.rowHeight != 0) {
                      colDiv.innerHTML = `<span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">${colDiv.innerHTML}</span>`;
                    }
                  }
                } else {
                  let addButton = this.adjustedAdditionalButtons.filter((b) => b['index'] == ci);

                  if (addButton.length > 0) {
                    let buttonText = addButton[0]['text'];
                    colDiv.appendChild(this.createButton(buttonText, g['_rowid']));
                  }

                  if (this.showSelectedCheckBox && ci == 0) {
                    // Add the show selectedcheckbox
                    colDiv.style['justifyContent'] = 'center';
                    colDiv.appendChild(this.createSelectedCheckbox());
                  }
                }
              } catch (error) {
                console.log(g, error);
              }

              colDiv.addEventListener('contextmenu', (e) => {
                this.showContextMenu = true;

                let mainTableRect = this.$refs['mainTable'].getBoundingClientRect();
                this.contextMenuList = ['Copy', 'Find'];
                this.contextMenuItem = { value: e.target.innerHTML, column: c, columnIndex: ci };

                let left = e['x'] - mainTableRect['left'];
                if (left + 200 > mainTableRect['width']) {
                  left -= 200;
                }
                let top = e['y'] - mainTableRect['top'];
                this.contextMenuPosition = { left: `${left}px`, top: `${top}px` };
              });
              rowDiv.appendChild(colDiv);
            }

            let overlay = document.createElement('div');
            overlay.classList.add('row-overlay');
            rowDiv.appendChild(overlay);

            this.scrollBlock.appendChild(rowDiv);
          });
        });
      } else {
        let rowCount = Array.from(this.scrollBlock.getElementsByClassName('table-row')).length;
        if (this.rowEnd > this.showData.length) {
          this.rowEnd = this.showData.length;
        } else if (this.rowEnd == this.showData.length && rowCount > 0 && rowCount == this.showData.length) { //added last condition because if table data count is exactly 75, it wont show the full table data, only show until 50, same goes for the number increase by 25. 100, 125, 150, ...
          return;
        }

        if (rowCount == this.showData.length) {
          return;
        }

        for (let i = this.rowStart; i < this.rowEnd; i++) {
          let index = this.pSelectedObject.findIndex((s) => (s['_rowid'] && s['_rowid'] == this.showData[i]['_rowid']) || JSON.stringify(s) == JSON.stringify(this.showData[i]));
          var rowDiv = document.createElement('div');
          rowDiv.classList.add('table-row', `${this.theme}-theme-row`);
          rowDiv.setAttribute('id', `${this.showData[i]['_rowid']}`);
          if (index >= 0) {
            rowDiv.classList.add(`${this.theme}-theme-row-selected`);
          }
          rowDiv.addEventListener('click', (e) => {
            this.rowClicked(e, e.currentTarget, this.showData[i]);
          });
          rowDiv.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            if (this.attachmentKey) {
              let mainTable = this.$refs['mainTable'].getBoundingClientRect();
              this.showContextMenu = true;
              let left = e.x - mainTable.left;
              if (e.x + 200 > mainTable.left + mainTable.width) {
                left -= 200;
              }
              this.contextMenuPosition['left'] = `${left}px`;
              this.contextMenuPosition['top'] = `${e.y - mainTable.top}px`;
              this.contextMenuItem = this.showData[i];
            }
          });

          for (let [ci, c] of this.getColumnDetails()['columns'].entries()) {
            if (this.hideColumns.includes(c) || this.appliedColumnOrderViewHiddenColumns.includes(c)) {
              continue;
            }
            var colDiv = document.createElement('div');
            colDiv.classList = ['row-data'];
            if (this.rowHeight > 0) {
              colDiv.style.maxHeight = `${this.rowHeight}px`;
              colDiv.style.minHeight = `${this.rowHeight}px`;
            }

            if (Object.keys(this.tableDataType).includes(c)) {
              if (this.tableDataType[c] == 'number' || this.tableDataType[c] == 'currency' || this.tableDataType[c] == 'rate') {
                colDiv.style.textAlign = 'right';
                colDiv.style.justifyContent = 'flex-end';
              }
            }

            if (this.stickColumn.includes(ci)) {
              colDiv.style.position = 'sticky';
              colDiv.style.left = '0';

              if (i % 2 == 0) {
                switch (this.theme) {
                  case 'red':
                    colDiv.style.backgroundColor = 'rgb(255, 235, 235)';
                    break;
                  case 'blue':
                    colDiv.style.backgroundColor = 'rgb(235, 235, 255)';
                    break;
                  case 'green':
                    colDiv.style.backgroundColor = '235, 255, 235';
                    break;
                  default:
                    colDiv.style.backgroundColor = 'white';
                    break;
                }
              } else {
                colDiv.style.backgroundColor = 'white';
              }
            }

            try {
              if (this.showData[i][c] != undefined) {
                let addIcon = this.additionalIconsIndex.includes(ci);

                if (addIcon) {
                  let key = Object.keys(this.showData[i])[ci];
                  let icon = this.showData[i][key];

                  if (icon) {
                    colDiv.appendChild(this.createIcon(icon, 20));
                  }
                } else {
                  if (this.tableDataType[c] == 'currency' || this.tableDataType[c] == 'rate' || this.tableDataType[c] == 'number') {
                    let value = this.getNumberValue(this.showData[i][c]);
                    colDiv.innerHTML = this.allowEmptyNumeric && (this.showData[i][c] == null || this.showData[i][c] === '') ? '' : this.tableDataType[c] == 'currency' ? this.$currencyFormat(value) : this.tableDataType[c] == 'rate' ? this.$currencyFormat(value, 4) : value;
                  } else if (this.tableDataType[c] == 'date' || this.tableDataType[c] == 'datetime') {
                    let allTags = this.getAllTags(this.showData[i][c]);
                    let value = '';
                    if (this.showData[i][c]) {
                      value = `${new Date(this.replaceAllTag(this.showData[i][c])).formatString(this.tableDataType[c] == 'date' ? this.$store.state['preferredDateFormat'] : `${this.$store.state['preferredDateFormat']} HH:mm`) || ''} ${allTags.flat().join(' ')}`;
                    }

                    colDiv.innerHTML = value;
                  } else {
                    colDiv.innerHTML = this.showData[i][c];
                  }

                  if (this.rowHeight != 0) {
                    colDiv.innerHTML = `<span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">${colDiv.innerHTML}</span>`;
                  }
                }
              } else {
                let addButton = this.adjustedAdditionalButtons.filter((b) => b['index'] == ci);

                if (addButton.length > 0) {
                  let buttonText = addButton[0]['text'];
                  colDiv.appendChild(this.createButton(buttonText, this.showData[i]['_rowid']));
                }

                if (this.showSelectedCheckBox && ci == 0) {
                  // Add the show selectedcheckbox
                  colDiv.style['justifyContent'] = 'center';
                  let cb = this.createSelectedCheckbox();
                  cb.checked = index >= 0;
                  colDiv.appendChild(cb);
                }
              }
            } catch (error) {
              console.log(i, this.showData[i], error);
            }

            colDiv.addEventListener('contextmenu', (e) => {
              this.showContextMenu = true;

              let mainTableRect = this.$refs['mainTable'].getBoundingClientRect();
              this.contextMenuList = ['Copy', 'Find'];
              this.contextMenuItem = { value: e.target.innerHTML, column: c, columnIndex: ci };

              let left = e['x'] - mainTableRect['left'];
              if (left + 200 > mainTableRect['width']) {
                left -= 200;
              }
              let top = e['y'] - mainTableRect['top'];
              this.contextMenuPosition = { left: `${left}px`, top: `${top}px` };
            });
            rowDiv.appendChild(colDiv);
          }

          let overlay = document.createElement('div');
          overlay.classList.add('row-overlay');
          rowDiv.appendChild(overlay);

          this.scrollBlock.appendChild(rowDiv);
        }

        if (clear) {
          this.scrollBlock.scrollTo(scrollX, 0);
        }

        if (this.rowEnd == this.showData.length) {
        } else {
          this.rowStart = this.rowEnd;
          this.rowEnd += 25;
        }
      }

      if (firstTime) {
        let column = Array.from(this.headerBlock.querySelectorAll('.table-column'));
        this.colWidth.forEach((w, wi) => {
          if (this.fitContentWidth) column[wi].style.width = `${w}px`;
          column[wi].style.minWidth = `${w}px`;
          column[wi].style.maxWidth = `${w}px`;
        });
      }

      this.resizeAllColumns();
    },
    getNumberValue: function (value) {
      let number = Number(value);
      return isNaN(number) ? 0 : number;
    },
    mainScroll: function (e) {
      let el = e.target;
      if (el.scrollLeft == this.leftScroll) {
        if (el.scrollTop + 10 >= el.scrollHeight - el.offsetHeight) {
          if (this.currentRowCount != -1 || this.groupBy) {
            return;
          }
          this.populateData();
        }
      } else {
        this.leftScroll = el.scrollLeft;
      }
    },
    searchColumn: function (e, index) {
      let input = e.target;
      let searchLimit = input.getAttribute('searchLimit');
      let colName = this.columnArrange[index];
      let searchIndex = this.columnSearch.findIndex((c) => c['column'] == colName);

      if (searchIndex == -1) {
        this.columnSearch.push({
          column: colName,
          lower: searchLimit == 'lower' ? input.value : '',
          upper: searchLimit == 'upper' ? input.value : '',
          type: input.type,
        });
      } else {
        this.columnSearch[searchIndex][searchLimit] = input.value;
      }

      let emptySearch = this.columnSearch.filter((s) => !s['lower'] && !s['upper']);
      emptySearch.forEach((e) => {
        let emptyIndex = this.columnSearch.indexOf(e);
        this.columnSearch.splice(emptyIndex, 1);
      });

      this.showData = this.privateTableData.slice();
      if (this.hasDataType()) {
        this.showData = this.showData.slice(1);
      }

      this.columnSearch.forEach((c) => {
        switch (c['type']) {
          case 'text':
            this.showData = this.showData.filter((s) => {
              if (s[c['column']]) {
                return s[c['column']].toString().toLowerCase().includes(c['lower'].toString().toLowerCase());
              }
            });
            break;
          case 'number':
            let lowerValue = this.getNumberValue(this.$currencyToNumber(c['lower']));
            let upperValue = this.getNumberValue(this.$currencyToNumber(c['upper']));

            if (c['lower'] && !c['upper']) {
              // More than
              this.showData = this.showData.filter(
                (s) => this.getNumberValue(this.$currencyToNumber(s[c['column']])) >= lowerValue,
              );
            } else if (!c['lower'] && c['upper']) {
              // Less than
              this.showData = this.showData.filter(
                (s) => this.getNumberValue(this.$currencyToNumber(s[c['column']])) <= upperValue,
              );
            } else {
              // In between
              this.showData = this.showData.filter(
                (s) =>
                  this.getNumberValue(this.$currencyToNumber(s[c['column']])) >= lowerValue &&
                  this.getNumberValue(this.$currencyToNumber(s[c['column']])) <= upperValue,
              );
            }
            break;
          case 'date':
            let lowerDate = Date.parse(c['lower']);
            let upperDate = Date.parse(c['upper']);

            this.showData = this.showData.filter((s) => {
              let currentValue = this.replaceAllTag(s[c['column']]);
              if (currentValue.length >= 3) {
                currentValue = `${currentValue.substring(0, currentValue.length - 2)}:${currentValue.substring(currentValue.length - 2, currentValue.length)}`;
              }

              let dataDate = Date.parse(currentValue);
              if (dataDate) {
                let dateCompare = new Date(dataDate);
                dateCompare.setHours(0, 0, 0, 0);

                if (!isNaN(lowerDate)) {
                  lowerDate = new Date(lowerDate);
                  lowerDate.setHours(0, 0, 0, 0);
                }

                if (!isNaN(upperDate)) {
                  upperDate = new Date(upperDate);
                  upperDate.setHours(0, 0, 0, 0);
                }

                if (lowerDate && isNaN(upperDate)) {
                  // More than
                  return new Date(dateCompare) >= lowerDate;
                } else if (isNaN(lowerDate) && upperDate) {
                  // Less than
                  return new Date(dateCompare) <= upperDate;
                } else {
                  // In between
                  return new Date(dateCompare) >= lowerDate && new Date(dateCompare) <= upperDate;
                }
              }
            });
            break;
        }
      });

      this.populateData(true);
    },
    inputChanged: function (e, index) {
      if (this.timer) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        this.searchColumn(e, index);
      }, 300);
    },
    changeRows: async function (e) {
      let parent = e.srcElement.parentElement;
      let el = e.target;

      for (let i = 0; i < 5; i++) {
        parent.children[i].classList.remove(`active-${this.theme}-theme`);
      }

      el.classList.add(`active-${this.theme}-theme`);

      this.currentRowCount = el.innerHTML == 'All' ? -1 : parseInt(el.innerHTML);

      if (this.currentRowCount != -1) {
        this.totalPages = Math.ceil(this.privateTableData.length / this.currentRowCount);
      }
      this.currentPage = 1;

      if (this.apiEndpoint && this.apiPayload) {
        await this.getSSRRows();
      }

      this.populateData(true);
    },
    changeRowsV2: function () {
      switch (this.currentRowCount) {
        case -1:
          this.currentRowCount = 10;
          break;
        case 10:
          this.currentRowCount = 20;
          break;
        case 20:
          this.currentRowCount = 30;
          break;
        case 30:
          this.currentRowCount = 50;
          break;
        case 50:
          this.currentRowCount = -1;
          break;
      }
    },
    pageClicked: async function (e) {
      switch (e) {
        case 'first':
          this.currentPage = 1;
          break;
        case 'prev':
          if (this.currentPage > 1) {
            this.currentPage--;
          }
          break;
        case 'next':
          if (this.currentPage < this.totalPages) {
            this.currentPage++;
          }
          break;
        case 'last':
          this.currentPage = this.totalPages;
          break;
        default:
          this.currentPage = parseInt(e);
          break;
      }

      if (this.apiEndpoint && this.apiPayload) {
        await this.getSSRRows();
      }

      this.populateData(true);
    },
    allRowClicked: async function () {
      if (this.selectedRow.length != 0) {
        let tempSelectedRow = [];
        this.$nextTick(() => {
          this.$emit('update:selectedRow', tempSelectedRow);
        });
      } else {
        let tempSelectedRow = [];
        this.$emit('update:selectedRow', []);
        this.showData.forEach((c) => tempSelectedRow.push(c));
        this.$nextTick(() => {
          this.$emit('update:selectedRow', tempSelectedRow);
        });
      }
    },
    rowClicked: async function (event, el, data) {
      if (this.maxSelection == 0) {
        return;
      }

      if (this.pSelectedObject.length > 0 && event['shiftKey']) {
        // With shift key is clicked
        let lastRow = this.pSelectedObject[this.pSelectedObject.length - 1]['_rowid'];
        let currentRow = data['_rowid'];

        let lastRowIndex = this.showData.findIndex((s) => s['_rowid'] == lastRow);
        let currentRowIndex = this.showData.findIndex((s) => s['_rowid'] == currentRow);

        let startIndex = Math.min(lastRowIndex, currentRowIndex) + 1;
        let endIndex = Math.max(lastRowIndex, currentRowIndex);

        while (startIndex <= endIndex) {
          if (this.maxSelection > 0 && this.pSelectedObject.length == this.maxSelection) {
            this.$showAlert(
              'error',
              'Max Row Count Reached',
              `Only ${this.maxSelection} rows(s) are allowed to be selected in this table`,
            );
            break;
          }

          let elToSelect = this.scrollBlock.children[startIndex + 1];
          if (!elToSelect.classList.contains(`${this.theme}-theme-row-selected`)) {
            elToSelect.classList.add(`${this.theme}-theme-row-selected`);
            this.pSelectedObject.push(this.showData[startIndex]);
          }
          startIndex++;
        }
        this.$emit('update:selectedRow', this.pSelectedObject);
        this.$emit('rowSelected', data, true);
      } else {
        // Normal selection
        let cb = null;
        if (this.showSelectedCheckBox) {
          cb = el.children[0].children[0];
        }

        if (Array.from(el.classList).includes(`${this.theme}-theme-row-selected`)) {
          el.classList.remove(`${this.theme}-theme-row-selected`);
          var index = this.pSelectedObject.findIndex((s) => (s['_rowid'] && s['_rowid'] == data['_rowid']) || JSON.stringify(s) == JSON.stringify(data));
          this.pSelectedObject.splice(index, 1);
          this.$emit('rowSelected', data, false);

          if (cb) {
            cb.checked = false;
          }
        } else {
          if (this.maxSelection > 0) {
            // can select 1 to maxSelection
            if (this.pSelectedObject.length >= this.maxSelection) {
              if (this.rollSelection) {
                let lastSelect = this.scrollBlock.querySelector(`#${this.pSelectedObject[0]['_rowid']}`);
                if (lastSelect) {
                  lastSelect.classList.remove(`${this.theme}-theme-row-selected`);

                  if (this.showSelectedCheckBox) {
                    let lastCB = (lastSelect.children[0].children[0].checked = false);
                  }
                }
                this.pSelectedObject.splice(0, 1);
              } else {
                let overlay = el.querySelector('.row-overlay');
                overlay.classList.add('row-overlay-error');
                overlay.style.maxWidth = 'calc(100% - 1px)';
                overlay.innerHTML = `Only ${this.maxSelection} item can be selected`;

                await this.$sleep(1000);
                overlay.style.maxWidth = '0';
                return;
              }
            }
          }

          if (!el.classList.value.includes('table-block')) {
            el.classList.add(`${this.theme}-theme-row-selected`);
            this.pSelectedObject.push(data);
            this.$emit('update:selectedRow', this.pSelectedObject);
            this.$emit('rowSelected', data, true);

            if (cb) {
              cb.checked = true;
            }
          }
        }
      }
    },
    clearSelectedClicked: function () {
      this.pSelectedObject.forEach((s) => {
        let el = this.scrollBlock.querySelector(`#${s['_rowid']}`);
        if (el) {
          el.classList.remove(`${this.theme}-theme-row-selected`);
        }
      });

      this.pSelectedObject = [];
      this.viewSelectedSection = false;
      this.$emit('update:selectedRow', this.pSelectedObject);
    },
    showSelectedClicked: function (e) {
      this.showSelectedOnly = !this.showSelectedOnly;
      var btn = e.target.innerHTML;

      if (btn == 'Show Selected Only') {
        this.showData = this.pSelectedObject;
        this.populateData(true);
      } else {
        this.showData = this.privateTableData.slice();
        this.populateData(true);
      }
    },
    downloadCSV: async function () {
      if (!this.allowExport) return;
      let downloadAll = true;
      let tempPrivateTableData = this.hasDataType() ? this.privateTableData.length - 1 : this.privateTableData.length;
      if (tempPrivateTableData != this.showData.length && this.showData.length > 0) {
        let downloadSelection = await this.$showAlert(
          'info',
          'Exporting to CSV',
          `You are about to download the data from this table to CSV.<br><br>Would you like to download<br><b>all the rows (${this.privateTableData.length} rows)</b><br>or<br>the <b>filtered rows only (${this.showData.length} rows)</b>?`,
          [
            { text: `Download All (${this.privateTableData.length} rows)` },
            { text: `Download Filtered (${this.showData.length} rows)` },
            { text: `Cancel`, color: 'gray', textColor: 'white' },
          ],
        );

        if (downloadSelection == 'Cancel') return;
        else if (downloadSelection.includes('Download Filtered')) downloadAll = false;
      }

      const visibleColumns = this.columnName
        .filter((column) => {
          return !this.hideColumns.includes(column) && !this.appliedColumnOrderViewHiddenColumns.includes(column); // remove hidden columns
        })
        .filter((column) => {
          return column !== 'table_box_autobutton'; // remove the "table_box_autobutton" column, which is auto generated if something was passed into the "additionalButtons" prop
        });

      // filter out hidden columns so they're not included in the CSV download

      let outputCSV = downloadAll ? `${this.title}\r\n\n` : `${this.title}_${this.showData.length}_rows\r\n\n`;
      outputCSV += `${visibleColumns.join(',')}\r\n`;

      let dataToDownload = downloadAll ? this.privateTableData : this.showData;
      const dataTypeIndex = dataToDownload.findIndex((row) => {
        return row['#'] === 0 || row['#'] === '0';
      });
      if (dataTypeIndex !== -1) {
        dataToDownload = dataToDownload.slice();
        dataToDownload.splice(dataTypeIndex, 1);
      }

      dataToDownload.forEach((s) => {
        let rowArray = [];
        visibleColumns.forEach((c) => {
          let value = this.escapeCSVField(this.replaceAllTag(s[c]));
          rowArray.push(value);
        });
        outputCSV += `${rowArray.join(',')}\r\n`;
      });

      if (Object.keys(this.dataType).length > 0) {
        let allKeys = Object.keys(dataToDownload[0]);
        let allNumeric = Object.keys(this.dataType).filter(
          (d) => this.dataType[d] == 'number' || this.dataType[d] == 'currency' || this.dataType[d] == 'rate',
        );
        let totalArray = [];
        allKeys.forEach((a) => {
          if (allNumeric.includes(a)) {
            totalArray.push(`${dataToDownload.map((d) => Number(d[a]) || 0).reduce((a, b) => a + b, 0)}`);
          } else {
            totalArray.push('');
          }
        });
        outputCSV += `${totalArray.join(',')}\r\n`;
      }

      let uri = 'data:text/csv;charset=utf-8,' + escape(outputCSV);
      let link = document.createElement('a');
      link.href = uri;
      link.style = 'visibility:hidden';
      link.download =
        this.downloadName ? `${this.downloadName.split(' ').join('_')}.csv` : `${this.title.split(' ').join('_')}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadExcel: async function () {
      if (!this.allowExport) return;
      let downloadAll = true;
      let tempPrivateTableData = this.hasDataType() ? this.privateTableData.length - 1 : this.privateTableData.length;
      if (tempPrivateTableData != this.showData.length && this.showData.length > 0) {
        let downloadSelection = await this.$showAlert(
          'info',
          'Exporting to Excel',
          `You are about to download the data from this table to excel.<br><br>Would you like to download<br><b>all the rows (${this.privateTableData.length} rows)</b><br>or<br>the <b>filtered rows only (${this.showData.length} rows)</b>?`,
          [
            { text: `Download All (${this.privateTableData.length} rows)` },
            { text: `Download Filtered (${this.showData.length} rows)` },
            { text: `Cancel`, color: 'gray', textColor: 'white' },
          ],
        );

        if (downloadSelection == 'Cancel') return;
        else if (downloadSelection.includes('Download Filtered')) downloadAll = false;
      }

      let dataToDownload = downloadAll ? this.privateTableData : this.showData;
      const dataTypeIndex = dataToDownload.findIndex((row) => {
        return row['#'] === 0 || row['#'] === '0';
      });
      if (dataTypeIndex !== -1) {
        dataToDownload = dataToDownload.slice();
        dataToDownload.splice(dataTypeIndex, 1);
      }

      const visibleColumns = this.columnName
        .filter((column) => {
          return !this.hideColumns.includes(column) && !this.appliedColumnOrderViewHiddenColumns.includes(column); // remove hidden columns
        })
        .filter((column) => {
          return column !== 'table_box_autobutton'; // remove the "table_box_autobutton" column, which is auto generated if something was passed into the "additionalButtons" prop
        });

      dataToDownload = dataToDownload.map((data) => {
        let dataWithVisibleColumnsOnly = {};
        for (const column of visibleColumns) {
          dataWithVisibleColumnsOnly[column] = this.removeTagWhenDownload(data[column]); // only take the visible columns, exclude hidden columns and the "_rowid" column
        }
        return dataWithVisibleColumnsOnly;
      });

      this.$fullscreenLoading('Generating Excel');
      await this.$jsonToExcel(
        downloadAll ? this.title : `${this.title}_${this.showData.length}_rows`,
        dataToDownload,
        this.dataType,
        this.tableFilters,
        true,
        this.allowEmptyNumeric,
      );
      this.$fullscreenLoading();
    },
    getColumnLetter: function (index) {
      let letters = [];

      let start = parseInt(index / 26);
      let letter = index % 26;
      letters.splice(0, 0, this.excelColumnLetters[letter]);
      while (start > 0) {
        letter = start % 26;
        letters.splice(0, 0, this.excelColumnLetters[letter - 1]);
        start -= 26;
      }

      return letters.join('');
    },
    removeTagWhenDownload: function (item) {
      if (item === undefined || item === null) return '';
      if (typeof item !== 'string') return item;
      return item.replace(/(<)(.*?)(>)/g, '');
    },

    columnOrderTooltipClicked: function () {
      this.showColumnOrderViews = true;
      this.columnOrderPopupViewsTableBoxSelectedRow = [];
    },
    populateColumnOrderPopupsWithCurrentArrangement: function () {
      // gets the current column arrangement and populates it into the popups
      this.columnReorderingMenuColumnOrder = this.getColumnDetails().columns.filter(
        (column) => column !== 'table_box_autobutton',
      );
      this.newColumnPopupColumns = this.allToggleableColumnsInTableData
        .filter(
          (toggleableColumn) =>
            !this.columnReorderingMenuColumnOrder.some((columnName) =>
              this.caseInsensitiveStringComparison(columnName, toggleableColumn),
            ),
        )
        .map((toggleableColumn) => ({ 'Column Name': toggleableColumn }));
    },
    columnOrderViewsNewViewClicked: function (pointerEvent) {
      this.columnReorderingMenuAction = 'N';
      this.columnReorderingMenuViewNameInputValue = '';
      this.columnReorderingMenuViewNameErrorMessage = '';
      this.columnReorderingMenuUserOwnedIsChecked = true;
      this.columnReorderingMenuDefaultIsChecked = false;
      this.populateColumnOrderPopupsWithCurrentArrangement();
      this.columnReorderingMenuSelectedColumn = '';
      this.showColumnOrderViews = false;
      this.showColumnReorderingMenu = true;
    },
    columnOrderViewsEditViewClicked: function (pointerEvent) {
      if (this.columnOrderPopupViewsTableBoxSelectedRow.length === 0) {
        this.$showAlert('error', 'No View Selected', 'Select a view to edit', [{ text: 'OK' }]);
        return;
      }

      this.columnReorderingMenuAction = 'E';
      const selectedView = this.columnOrderPopupViewsTableBoxSelectedRow[0];
      this.columnReorderingMenuViewNameInputValue = selectedView['View Name'];
      this.columnReorderingMenuViewNameErrorMessage = '';
      this.columnReorderingMenuUserOwnedIsChecked = selectedView.Owner === this.loginDetails.LoginUserId; // if there's ever a user with the same userid as the company short name, this will cause issues, but that's very unlikely
      this.columnReorderingMenuDefaultIsChecked = selectedView.Default === 'Yes';

      if (selectedView.Applied === 'Yes') {
        this.populateColumnOrderPopupsWithCurrentArrangement();
      } else {
        // display the column arrangement of the saved view
        // remove columns that are hardcoded as hidden, these could have changed since the last time the user saved the view
        // remove columns that aren't found in the tableData, could happen due to the API response changing since the last time the user saved the view
        // add columns that are in the tableData but not in the saved view if they are not hardcoded as hidden
        let columnOrder = [];
        let hiddenColumns = [];
        let allToggleableColumns = [...this.allToggleableColumnsInTableData];
        for (const { 'Column Name': columnName, Show } of selectedView.Columns) {
          const columnIndex = allToggleableColumns.findIndex((toggleableColumn) =>
            this.caseInsensitiveStringComparison(columnName, toggleableColumn),
          );
          if (columnIndex === -1) {
            continue;
          }

          allToggleableColumns.splice(columnIndex, 1);
          if (Show === '1') {
            columnOrder.push(columnName);
          } else {
            hiddenColumns.push({ 'Column Name': columnName });
          }
        }

        this.columnReorderingMenuColumnOrder = columnOrder.concat(allToggleableColumns);
        this.newColumnPopupColumns = hiddenColumns;
      }

      this.columnReorderingMenuSelectedColumn = '';
      this.showColumnOrderViews = false;
      this.showColumnReorderingMenu = true;
    },
    columnOrderViewsDeleteViewClicked: async function (pointerEvent) {
      if (this.columnOrderPopupViewsTableBoxSelectedRow.length === 0) {
        this.$showAlert('error', 'No View Selected', 'Select a view to delete', [{ text: 'OK' }]);
        return;
      }

      const selectedView = this.columnOrderPopupViewsTableBoxSelectedRow[0];
      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete View',
        `Are you sure you want to delete View <b>${selectedView['View Name']}</b>`,
        [{ text: 'Delete', textColor: 'white', color: 'red' }, { text: 'Cancel' }],
      );

      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        formname: this.columnOrderID.formname,
        formctrlname: this.columnOrderID.formctrlname,
        viewname: selectedView['View Name'],
        userowned: selectedView.Owner === this.loginDetails.LoginUserId, // if there's ever a user with the same userid as the company short name, this will cause issues, but that's very unlikely
      };

      const saveColumnOrderResult = await this.$apiCallV2('/TableBox/SaveColumnOrder', requestBody);

      if (saveColumnOrderResult[0].Results !== '0') {
        this.$showAlert(
          'error',
          'View NOT Deleted',
          `There was an error deleting View: ${saveColumnOrderResult[0].ErrMsg}`,
          [{ text: 'OK' }],
        );
        return;
      }

      this.$showAlert('success', 'View Deleted', 'Successfully deleted View', [{ text: 'OK' }]);

      // API will return the updated views after a view has been successfully deleted
      // if a view that wasn't applied was deleted, nothing else should happen, the column order should remain as is
      // if the deleted view was applied, then a new view needs to be selected from the remaining views to be applied
      this.showColumnOrderViews = false;
      this.columnOrderPopupViewsTableBoxSelectedRow = [];
      const updatedViews = JSON.parse(saveColumnOrderResult[0].UpdatedViews);
      const updatedViewsWithDefault = this.identifyDefaultView(
        updatedViews,
        selectedView.Applied !== 'Yes' ? selectedView['View Name'] : null,
      );
      this.$emit('update:columnOrderViews', updatedViewsWithDefault);
      await this.$nextTick();
      this.showColumnOrderViews = true;

      // deleting all views won't revert the columns back to the original order based on the API response
      // however after deleting the last view, all hidden columns will reappear, and the user can proceed create a new view with all the columns shown
      // couldn't figure out a way to revert the column order because privateTableData will still remain as ordered by the last deleted view
      // and it can't pull the column order from tableData since some parents pages will sync updates for that prop, which means the parent page will also have the reordered data, not the original
    },
    columnOrderViewsApplyClicked: function (pointerEvent) {
      if (this.columnOrderPopupViewsTableBoxSelectedRow.length === 0) {
        this.$showAlert('error', 'No View Selected', 'Select a view to apply', [{ text: 'OK' }]);
        return;
      }

      const selectedView = this.columnOrderPopupViewsTableBoxSelectedRow[0];
      if (selectedView.Applied === 'Yes') {
        // already applied
        this.showColumnOrderViews = false; // no need to do anything
        this.$showAlert('warning', 'View Already Applied', `View ${selectedView['View Name']} was already applied`, [
          { text: 'OK' },
        ]);
        return;
      }

      this.$emit(
        'update:columnOrderViews',
        this.columnOrderViews.map((view) => ({
          ...view,
          Applied: view['View Name'] === selectedView['View Name'] ? 'Yes' : '',
        })),
      );
      this.showColumnOrderViews = false;
      this.$showAlert('success', 'View Applied', `Successfully applied View ${selectedView['View Name']}`, [
        { text: 'OK' },
      ]);
    },
    columnOrderViewsCloseClicked: function (pointerEvent) {
      this.showColumnOrderViews = false;
    },
    columnReorderingMenuNewColumnClicked: async function (pointerEvent) {
      if (this.newColumnPopupColumns.length === 0) {
        this.$showAlert('error', 'No Available Columns', 'All available columns have already been added to the view', [
          { text: 'OK' },
        ]);
        return;
      }

      this.newColumnPopupSelectedColumn = null;
      this.newColumnPopupErrorMessage = '';
      this.showNewColumnPopup = true;
    },
    columnReorderingMenuMoveUpClicked: function (pointerEvent) {
      if (this.columnReorderingMenuSelectedColumn === '') {
        this.$showAlert('error', 'No Column Selected', 'Please select a column to move', [{ text: 'OK' }]);
        return;
      }

      const selectedColumnIndex = this.columnReorderingMenuColumnOrder.findIndex((columnName) =>
        this.caseInsensitiveStringComparison(columnName, this.columnReorderingMenuSelectedColumn),
      );
      if (selectedColumnIndex === 0) {
        // already at the top of the list
        return;
      }

      this.columnReorderingMenuColumnOrder.splice(selectedColumnIndex, 1);
      this.columnReorderingMenuColumnOrder.splice(selectedColumnIndex - 1, 0, this.columnReorderingMenuSelectedColumn);
    },
    columnReorderingMenuMoveDownClicked: function (pointerEvent) {
      if (this.columnReorderingMenuSelectedColumn === '') {
        this.$showAlert('error', 'No Column Selected', 'Please select a column to move', [{ text: 'OK' }]);
        return;
      }

      const selectedColumnIndex = this.columnReorderingMenuColumnOrder.findIndex((columnName) =>
        this.caseInsensitiveStringComparison(columnName, this.columnReorderingMenuSelectedColumn),
      );
      if (selectedColumnIndex === this.columnReorderingMenuColumnOrder.length - 1) {
        // already at the bottom of the list
        return;
      }

      this.columnReorderingMenuColumnOrder.splice(selectedColumnIndex, 1);
      this.columnReorderingMenuColumnOrder.splice(selectedColumnIndex + 1, 0, this.columnReorderingMenuSelectedColumn);
    },
    columnReorderingMenuColumnClicked: function (columnName) {
      if (!this.caseInsensitiveStringComparison(columnName, this.columnReorderingMenuSelectedColumn)) {
        this.columnReorderingMenuSelectedColumn = columnName;
      } else {
        this.columnReorderingMenuSelectedColumn = '';
      }
    },
    columnReorderingMenuDeleteColumnClicked: async function (pointerEvent, columnToDelete) {
      pointerEvent.stopPropagation(); // to prevent the column from being toggled as selected or unselected

      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Column',
        `Are you sure you wish to delete the ${columnToDelete} column?`,
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray' },
        ],
      );
      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const indexOfColumnToDelete = this.columnReorderingMenuColumnOrder.findIndex((columnName) =>
        this.caseInsensitiveStringComparison(columnName, columnToDelete),
      );
      this.columnReorderingMenuColumnOrder.splice(indexOfColumnToDelete, 1);
      if (this.columnReorderingMenuSelectedColumn === columnToDelete) {
        // selected column was deleted
        this.columnReorderingMenuSelectedColumn = ''; // unselect it
      }
      this.newColumnPopupColumns.push({ 'Column Name': columnToDelete }); // add it back to allow users to re-add the column again
    },
    columnReorderingMenuSaveClicked: async function (pointerEvent) {
      const trimmedViewName = this.columnReorderingMenuViewNameInputValue.trim();
      if (trimmedViewName === '') {
        this.columnReorderingMenuViewNameErrorMessage = 'View Name is required!';
        return;
      }

      if (this.columnReorderingMenuColumnOrder.length === 0) {
        this.$showAlert('error', 'No Columns Added', 'Please add at least one column to the view', [{ text: 'OK' }]);
        return;
      }

      const requestBody = {
        action: this.columnReorderingMenuAction,
        formname: this.columnOrderID.formname,
        formctrlname: this.columnOrderID.formctrlname,
        userowned: this.columnReorderingMenuUserOwnedIsChecked,
        defposition: this.columnReorderingMenuDefaultIsChecked,
        shownColumns: this.columnReorderingMenuColumnOrder,
        hiddenColumns: this.newColumnPopupColumns.map((column) => column['Column Name']),
      };
      switch (this.columnReorderingMenuAction) {
        case 'N':
          requestBody.viewname = trimmedViewName;
          break;

        case 'E': {
          const viewToEdit = this.columnOrderPopupViewsTableBoxSelectedRow[0];
          requestBody.viewname = viewToEdit['View Name'];
          requestBody.editedviewname = trimmedViewName;
          break;
        }
      }

      this.setLoader('Saving View');
      const saveColumnOrderResult = await this.$apiCallV2('/TableBox/SaveColumnOrder', requestBody);
      this.setLoader();

      if (saveColumnOrderResult[0].Results !== '0') {
        if (this.columnReorderingMenuAction === 'N') {
          this.$showAlert(
            'error',
            'View NOT Saved',
            `There was an error creating View: ${saveColumnOrderResult[0].ErrMsg}`,
            [{ text: 'OK' }],
          );
        } else {
          this.$showAlert(
            'error',
            'View NOT Saved',
            `There was an error editing View: ${saveColumnOrderResult[0].ErrMsg}`,
            [{ text: 'OK' }],
          );
        }

        return;
      }

      if (this.columnReorderingMenuAction === 'N') {
        this.$showAlert('success', 'View Saved', 'Successfully created View', [{ text: 'OK' }]);
      } else {
        this.$showAlert('success', 'View Saved', 'Successfully edited View', [{ text: 'OK' }]);
      }
      // API will return the updated views after a view has been successfully saved
      // if there were no views before the newly saved view, then it should be applied automatically
      // otherwise, nothing should happen and the column order should stay as is according to the currently applied view

      this.showColumnOrderViews = true;
      this.showColumnReorderingMenu = false;
      this.columnOrderPopupViewsTableBoxSelectedRow = [];
      const updatedViews = JSON.parse(saveColumnOrderResult[0].UpdatedViews);
      const currentlyAppliedViewName = this.appliedColumnOrderView?.['View Name'];
      const updatedViewsWithDefault = this.identifyDefaultView(updatedViews, currentlyAppliedViewName);
      this.$emit('update:columnOrderViews', updatedViewsWithDefault);
      // the columnOrderViews watcher will take care updating the column order if necessary
    },
    columnReorderingMenuCancelClicked: function (pointerEvent) {
      this.showColumnReorderingMenu = false;
      this.showColumnOrderViews = true;
    },

    newColumnPopupButtonClicked: function (clickedButton) {
      if (clickedButton === 'Cancel') {
        this.showNewColumnPopup = false;
        return;
      }

      if (this.newColumnPopupSelectedColumn === null) {
        this.newColumnPopupErrorMessage = 'Column Name is required!';
        return;
      }

      const columnToAdd = this.newColumnPopupSelectedColumn['Column Name'];
      let indexToAddAt;
      if (this.columnReorderingMenuSelectedColumn !== '') {
        indexToAddAt =
          this.columnReorderingMenuColumnOrder.findIndex((columnName) =>
            this.caseInsensitiveStringComparison(columnName, this.columnReorderingMenuSelectedColumn),
          ) + 1;
      } else {
        indexToAddAt = this.columnReorderingMenuColumnOrder.length;
      }
      this.columnReorderingMenuColumnOrder.splice(indexToAddAt, 0, columnToAdd);

      const indexToRemove = this.newColumnPopupColumns.findIndex(({ 'Column Name': columnName }) =>
        this.caseInsensitiveStringComparison(columnName, columnToAdd),
      );
      this.newColumnPopupColumns.splice(indexToRemove, 1);
      this.newColumnPopupSelectedColumn = null;

      this.showNewColumnPopup = false;
    },
    setLoader: function (loaderText) {
      if (!loaderText) {
        this.loaderIsVisible = false;
      } else {
        this.loaderIsVisible = true;
        this.loaderText = loaderText;
      }
    },

    getColumnReorderingMenuColumnCardClasses: function (columnName) {
      const CSSClasses = ['column-card'];

      if (!this.caseInsensitiveStringComparison(columnName, this.columnReorderingMenuSelectedColumn)) {
        return CSSClasses;
      } else {
        return CSSClasses.concat('selected');
      }
    },
    caseInsensitiveStringComparison: function (firstString, secondString) {
      return firstString.toLowerCase() === secondString.toLowerCase();
    },

    createButton: function (text, id) {
      let btn = document.createElement('button');
      btn.innerHTML = text;

      const buttonConfig = this.adjustedAdditionalButtons.find(b => b.text === text);
      const row = this.showData.find(d => d['_rowid'] == id);
      btn.disabled = typeof buttonConfig?.disabled === 'function' ? !!buttonConfig.disabled(row) : !!buttonConfig?.disabled;
      if (buttonConfig && buttonConfig.ref) {
        btn.setAttribute('data-ref', buttonConfig.ref);
        btn.classList.add('table-button-ref');
      }

      btn.addEventListener('click', (pointerEvent) => {
        pointerEvent.stopPropagation(); // to stop the row from being selected/deselected when the user clicks on a button
        if (btn.disabled) return;
        var data = this.showData.filter((d) => d['_rowid'] == id);
        if (data.length > 0) {
          this.$emit('buttonClicked', text, data[0]);
        }
      });
      return btn;
    },
    createIcon: function (src, size) {
      let img = document.createElement('img');
      img.src = src;
      img.height = size;
      img.width = size;

      return img;
    },
    createGroupingHead: function (text, length) {
      let div = document.createElement('div');
      div.classList.add('group-header');

      let icon = document.createElement('img');
      icon.src = 'https://dsmlib.logistics.systems/Icons/Sort.png';
      icon.style.transform = 'scaleY(-1)';
      icon.style.pointerEvents = 'none';

      let title = document.createElement('div');
      title.innerHTML = text;
      title.style.pointerEvents = 'none';

      div.appendChild(icon);
      div.appendChild(title);

      div.addEventListener('click', (e) => {
        let target = e.target;
        let index = Array.from(this.scrollBlock.children).indexOf(target);
        let start = index + 1;

        let icon = e.target.children[0];

        for (let i = start; i < start + length; i++) {
          this.scrollBlock.children[i].style.display = icon.style.transform == 'scaleY(-1)' ? 'none' : 'flex';
        }

        icon.style.transform = icon.style.transform == 'scaleY(-1)' ? 'scaleY(1)' : 'scaleY(-1)';
      });

      return div;
    },
    createSelectedCheckbox: function () {
      let cb = document.createElement('input');
      cb.setAttribute('type', 'checkbox');

      return cb;
    },

    resizerMouseDown: function (e) {
      this.resizeElement = e.target;
      this.resizeIndex = parseInt(this.resizeElement.id.split('_')[1]);
      this.resizeColumnElement = this.headerBlock.children[this.resizeIndex];
      this.oriColumnWidth = this.colWidth[this.resizeIndex];

      this.resizeDraggingX = e.pageX - this.resizeElement.getBoundingClientRect().left + 5;

      document.addEventListener('mousemove', this.resizerMouseMove);
      document.addEventListener('mouseup', this.resizerMouseUp);
    },
    resizerMouseMove: function (e) {
      let currentLeft =
        e.pageX - this.scrollBlock.getBoundingClientRect().left - this.resizeDraggingX + this.scrollBlock.scrollLeft;
      let columnLeft = this.resizeColumnElement.offsetLeft;
      this.newColumnWidth = currentLeft - columnLeft + 5;

      if (this.newColumnWidth >= 50) {
        this.colWidth[this.resizeIndex] = this.newColumnWidth;
        this.resizeColumnElement.style.maxWidth = `${this.newColumnWidth}px`;
        this.resizeColumnElement.style.minWidth = `${this.newColumnWidth}px`;
        this.resizeElement.style.left = `${currentLeft}px`;
      } else {
        this.newColumnWidth = 50;
      }
    },
    resizerMouseUp: function () {
      let change = this.newColumnWidth - this.oriColumnWidth;
      for (let i = this.resizeIndex + 1; i < this.colWidth.length; i++) {
        let thisLeft = parseInt(this.headerBlock.querySelector(`#resizer_${i}`).style.left);
        thisLeft += change;
        this.headerBlock.querySelector(`#resizer_${i}`).style.left = `${thisLeft}px`;
      }

      let table = Array.from(this.scrollBlock.children).splice(1);
      table.forEach((t) => {
        t.children[this.resizeIndex].style.maxWidth = `${this.newColumnWidth}px`;
        t.children[this.resizeIndex].style.minWidth = `${this.newColumnWidth}px`;
      });

      document.removeEventListener('mousemove', this.resizerMouseMove);
      document.removeEventListener('mouseup', this.resizerMouseUp);
    },
    placeResizer: function () {
      let allResizers = Array.from(this.headerBlock.querySelectorAll('.col-resizer'));
      let left = 0;
      this.colWidth.forEach((w, wi) => {
        left += w;
        allResizers[wi].style.left = `${left - 5}px`;
      });
    },

    headerMouseDown: function (e) {
      this.isHeaderMouseDown = true;
      this.$nextTick(() => {
        this.draggingElement = this.$refs['draggingContainer'].getBoundingClientRect();
      });

      let parent = e.target.parentNode;
      if (Array.from(parent.classList).includes('title-block')) {
        this.headerElement = parent.parentNode;
      } else {
        this.headerElement = parent;
      }

      this.draggingX = e.pageX - this.headerElement.getBoundingClientRect().left;

      this.mouseDownTime = new Date().getTime();

      let index = Array.from(this.headerElement.parentNode.children).indexOf(this.headerElement);
      this.startHeaderIndex = index;
      this.headerDraggingName = this.headerElement.getAttribute('columnname');
    },
    headerMouseMove: function (e) {
      let timeLength = new Date().getTime() - this.mouseDownTime;
      if (timeLength < 200) {
        return;
      }

      //#region Inserting placeholder block for the dragging column
      let dragRect = this.headerElement.getBoundingClientRect();
      this.placeholderElement.style.width = `${dragRect.width}px`;
      this.placeholderElement.style.height = `${dragRect.height}px`;
      this.placeholderElement.style.backgroundColor = `rgb(190,190,190)`;
      this.placeholderElement.style.border = `2px dashed rgb(128,128,128)`;
      this.headerElement.parentNode.insertBefore(this.placeholderElement, this.headerElement.nextSibling);
      //#endregion

      this.headerElement.style.position = 'absolute';
      this.headerElement.style.left = `${e.pageX - this.draggingElement.left - this.draggingX + this.$refs['scrollBlock'].scrollLeft}px`;
      this.headerElement.style.zIndex = `99`;

      let prevElement = this.headerElement.previousElementSibling;
      let nextElement = this.placeholderElement.nextElementSibling;

      let movingDirection = this.moveDirection(prevElement, this.headerElement, nextElement);
      if (movingDirection < 0) {
        // Moving left
        this.swapElement(prevElement, this.headerElement);
        this.swapElement(prevElement, this.placeholderElement);
      } else if (movingDirection > 0) {
        // Moving right
        this.swapElement(nextElement, this.placeholderElement);
        this.swapElement(nextElement, this.headerElement);
      }
    },
    headerMouseUp: function () {
      this.isHeaderMouseDown = false;
      let timeLength = new Date().getTime() - this.mouseDownTime;
      if (timeLength < 300) {
        this.sortTable();
        return;
      }

      let newIndex = this.getColumnDetails()['columns'].indexOf(this.headerDraggingName);
      if (this.startHeaderIndex != newIndex) {
        let allrows = Array.from(this.$refs['scrollBlock'].getElementsByClassName('table-row'));
        allrows.forEach((r) => {
          let elementToMove = r.children[this.startHeaderIndex];
          r.removeChild(elementToMove);
          r.insertBefore(elementToMove, r.children[newIndex]);
        });
      }

      this.headerElement.style.position = 'relative';
      this.headerElement.style.removeProperty('left');
      this.headerElement.style.removeProperty('z-index');
      this.headerElement.parentNode.removeChild(this.placeholderElement);

      this.headerDraggingName = null;
      this.headerElement = null;
      this.startHeaderIndex = null;
    },
    moveDirection: function (prev, curr, next) {
      let prevRect = prev ? prev.getBoundingClientRect() : null;
      let currRect = curr ? curr.getBoundingClientRect() : null;
      let nextRect =
        next ?
          Array.from(next.classList).includes('col-resizer') ?
            null
          : next.getBoundingClientRect()
        : null;

      if (prevRect && currRect.left < prevRect.left + prevRect.width / 2) {
        return -1; // Moving left
      } else if (nextRect && currRect.left + currRect.width > nextRect.left + nextRect.width / 2) {
        return 1; // Moving right
      } else {
        return 0; // Stay same position
      }
    },
    swapElement: function (el1, el2) {
      let parent1 = el1.parentNode;
      let sibling1 = el1.nextSibling === el2 ? el1 : el1.nextSibling;

      el2.parentNode.insertBefore(el1, el2);

      parent1.insertBefore(el2, sibling1);
    },
    swapArrayElement: function (array, from, to) {
      let temp = array[to];
      array[to] = array[from];
      array[from] = temp;
    },
    getColumnDetails: function () {
      let columns = Array.from(this.$refs['headerBlock'].children);
      let columnNames = columns.map((h) => h.getAttribute('columnname')).filter((h) => h);
      let columnWidth = columns.map((h) => this.fitContentWidth ? parseFloat(window.getComputedStyle(h).width) || h.offsetWidth : h.getBoundingClientRect().width);

      // this.additionalButtons.forEach(b => columnNames.splice(b['index'], 0, ''));
      this.additionalIconsIndex.forEach((i) => columnNames.splice(i, 0, ''));
      if (this.showSelectedCheckBox) {
        columnNames.splice(0, 0, '');
      }

      return {
        columns: columnNames,
        width: columnWidth,
      };
    },
    resizeAllColumns: function () {
      let columnWidth = this.getColumnDetails()['width'];
      if (this.fitContentWidth) {
        columnWidth = columnWidth.map((width, index) =>
          width || parseFloat(this.headerBlock.children[index].style.minWidth) || this.colWidth[index],
        );
      }
      let allrows = Array.from(this.$refs['scrollBlock'].getElementsByClassName('table-row'));
      allrows.forEach((r) => {
        let columns = Array.from(r.children);
        columns.forEach((c, i) => {
          if (this.fitContentWidth) c.style.width = `${columnWidth[i]}px`;
          c.style.minWidth = `${columnWidth[i]}px`;
          c.style.maxWidth = `${columnWidth[i]}px`;
        });
      });
    },

    resizeMouseDown: function (e, colName) {
      let columnDetails = this.getColumnDetails();
      if (this.resizeClickTime) {
        let difference = new Date().getTime() - this.resizeClickTime;
        if (difference < 250) {
          if (colName != 'table_box_autobutton') {
            let colIndex = columnDetails['columns'].indexOf(colName);
            let startWidth = columnDetails['width'][colIndex];

            let allValue = this.showData.map((s) => (s[colName] ? `${s[colName]}`.trim().length : 0));
            let newWidth = Math.max(...allValue) * 7.205 + 12;
            e.target.parentNode.style.maxWidth = `${newWidth}px`;
            e.target.parentNode.style.minWidth = `${newWidth}px`;

            this.resizeMouseUp();
          }
          return;
        }
      }

      this.resizeClickTime = new Date().getTime();

      let columns = columnDetails['columns'];
      this.isResizing = true;
      this.resizeObject = {
        el: e.target.parentNode,
        startSize: e.target.parentNode.getBoundingClientRect().width,
        startPosition: e.pageX,
        resizeIndex: columns.indexOf(colName),
      };
    },
    resizeMouseUp: function (e) {
      this.isResizing = false;

      let colIndex = this.resizeObject['resizeIndex'];
      let width = this.getColumnDetails()['width'];
      let allrows = Array.from(this.$refs['scrollBlock'].getElementsByClassName('table-row'));
      allrows.forEach((r) => {
        let col = r.children[colIndex];
        col.style.minWidth = `${width[colIndex]}px`;
        col.style.maxWidth = `${width[colIndex]}px`;
      });
    },
    resizeMouseMove: function (e) {
      let parent = this.resizeObject['el'];
      let size = this.resizeObject['startSize'] + (e.pageX - this.resizeObject['startPosition']);
      parent.style.minWidth = `${size}px`;
      parent.style.maxWidth = `${size}px`;
    },

    checkHiddenRows: function () {
      let allChild = Array.from(this.scrollBlock.children);
      allChild.forEach((c) => (c.style.display = 'flex'));

      let hideID = this.privateTableData
        .filter((t) => this.hideRow['values'].includes(t[this.hideRow['key']]))
        .map((h) => h['_rowid']);
      let toHide = allChild.filter((c) => hideID.includes(c['id']));
      toHide.forEach((h) => (h.style.display = 'none'));
    },

    downloadAttachmentClicked: function () {
      let attName = this.contextMenuItem[this.attachmentKey['name']];
      let attBase64 = this.contextMenuItem[this.attachmentKey['base64']];
      let attType = this.contextMenuItem[this.attachmentKey['type']];

      if (!attName || !attBase64 || !attType) {
        this.$showAlert('error', 'File Not Available', 'No available file to download for this item');
      } else {
        let anchor = document.createElement('a');
        anchor.href = `data:application/octet-stream;base64,${attBase64}`;
        anchor.download = attName.trim();
        anchor.click();
      }
      this.showContextMenu = false;
    },

    replaceAllTag: function (text, removeAll = false) {
      if (text === undefined || text === null) {
        // need to explicitly compare with undefined and null
        return ''; // so that falsy values like 0 don't get converted to empty strings
      }

      if (typeof text !== 'string') {
        // if it's an integer or a float
        return text; // don't do anything
      }

      let regex = [];
      if (removeAll) {
        const content = document.createElement('template');
        content.innerHTML = text;
        return content.content.textContent || '';
      } else {
        for (let t of this.tagToRemove) {
          regex.push(new RegExp(`(<${t})(.*?)(>)`, 'g'));
        }
      }

      for (let r of regex) {
        text = text.replace(r, '');
      }

      return text;
    },
    getAllTags: function (text) {
      let regex = [];
      for (let t of this.tagToRemove) {
        regex.push(new RegExp(`(<${t})(.*?)(>)`, 'g'));
      }

      if (!text) {
        text = '';
      }

      let result = [];
      for (let r of regex) {
        result.push(text.match(r));
      }

      return result;
    },

    escapeCSVField: function(value) {
      //qyvoon20260819 cater for checking csv specs
      if (value === null || value === undefined) {
        return '';
      }
      let str = String(value);
      // if the value contains a comma, double-quote, or newline, it needs wrapping
      if (/[",\r\n]/.test(str)) {
        str = `"${str.replace(/"/g, '""')}"`; // escape existing quotes by doubling them
      }
      return str;
    },

    contextMenuClicked: function (menu) {
      if (menu.includes('Copy')) {
        navigator.clipboard.writeText(this.contextMenuItem['value']);
      } else {
        let headerBlock = this.$refs['headerBlock'];
        let columnBlock = Array.from(headerBlock.children)[this.contextMenuItem['columnIndex']];
        let inputFields = columnBlock.getElementsByTagName('input');
        if (inputFields.length > 0) {
          inputFields[0]['value'] = this.contextMenuItem['value'];
          this.searchColumn({ target: inputFields[0] }, this.contextMenuItem['columnIndex']);
        }
      }

      this.showContextMenu = false;
    },

    //#region Size Changed
    sizeChanged: function (e) {
      let wide = e.length == 2 ? e[0]['contentRect']['width'] : null;

      if (wide) {
        this.widerTable = wide >= 10;
      }
    },
    //#endregion

    //#region SSR Api Call
    getSSRRows: async function () {
      this.apiPayload['page'] = this.currentPage;
      this.apiPayload['rowperpage'] = this.currentRowCount;
      this.apiPayload['pagination'] = '1';

      this.ssrLoading = true;
      let result = await this.$apiCallV2(`/TableBox/ServerSideRender?endpoint=${this.apiEndpoint}`, this.apiPayload);
      this.ssrLoading = false;

      this.showData = [];
      this.privateTableData = [];

      this.ssrRowCount = result['Data'].length;
      this.ssrTotalRows = Number(result['TableTotal'][0]['Rows']);
      this.ssrTotalPages = Number(result['TableTotal'][0]['TotalPages']);
      this.privateTableData = result['Data'];

      this.initTable();
    },
    //#endregion

    identifyDefaultView: function (allViews, preferredView) {
      if (!Array.isArray(allViews) || allViews.length === 0) {
        return [];
      }

      let defaultViewIndex;
      if (preferredView) {
        const preferredViewIndex = allViews.findIndex((view) => view['View Name'] === preferredView);
        if (preferredViewIndex !== -1) {
          defaultViewIndex = preferredViewIndex;
        }
      }

      if (defaultViewIndex === undefined) {
        const firstDefaultViewIndex = allViews.findIndex((view) => view.Default === 'Yes');
        if (firstDefaultViewIndex !== -1) {
          defaultViewIndex = firstDefaultViewIndex;
        }
      }

      if (defaultViewIndex === undefined) {
        defaultViewIndex = 0;
      }

      return allViews.map((view, index) => ({
        // used map to avoid mutating allViews directly
        ...view,
        Applied: index === defaultViewIndex ? 'Yes' : '',
      }));
    },
    reorderColumns: function (tableData, columnOrder) {
      if (!Array.isArray(tableData) || tableData.length === 0) {
        return [];
      }

      if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
        return tableData;
      }

      return tableData.map((row) => {
        const reorderedData = {};

        for (const { 'Column Name': columnName } of columnOrder) {
          if (!columnName || !Object.prototype.hasOwnProperty.call(row, columnName)) continue;

          // reorder columns, regardless of whether they should be shown or not
          // hiding of columns is done somewhere else
          reorderedData[columnName] = row[columnName];
        }

        return {
          ...reorderedData,
          ...row,
        };
      });
    },
  },
  async mounted() {
    this.isDevelopment = window.location.hostname == 'localhost';
    this.loginDetails = await this.$apiCallV2('/Login/GetDetails');

    let columnOrder;
    if (this.columnOrderViews.length === 0) {
      columnOrder = [];
    } else if (this.appliedColumnOrderViewColumns) {
      columnOrder = this.appliedColumnOrderViewColumns;
    } else {
      const columnOrderViewsWithDefault = this.identifyDefaultView(this.columnOrderViews);
      columnOrder = columnOrderViewsWithDefault.find((view) => view.Applied === 'Yes');
      this.$emit('update:columnOrderViews', columnOrderViewsWithDefault);
    }

    if (this.selectedRow.length > 0) {
      this.pSelectedObject = this.selectedRow;
    }

    if (this.tableData.length > 0) {
      this.privateTableData = this.reorderColumns(this.tableData, columnOrder);
      this.initTable();
      this.$nextTick(() => {
        if (!this.observer) {
          this.observer = new ResizeObserver(this.sizeChanged);
        }

        this.observer.observe(this.$refs['bottomSpacer']);
        this.observer.observe(this.$refs['bottomSpacer2']);
      });
    }

    if (this.apiEndpoint && this.apiPayload) {
      this.currentPage = 1;
      this.currentRowCount = 50;

      await this.getSSRRows(); // need to figure out how to get it working with SSR as well
      this.$nextTick(() => {
        if (!this.observer) {
          this.observer = new ResizeObserver(this.sizeChanged);
        }

        this.observer.observe(this.$refs['bottomSpacer']);
        this.observer.observe(this.$refs['bottomSpacer2']);
      });
    }
  },
  watch: {
    tableData: function (val) {
      this.privateTableData = val.map((row) => {
        let copy = Object.assign({}, row);
        Object.keys(copy).forEach((key) => {
          if (copy[key] === null) copy[key] = '';
        });
        return copy;
      });
      this.updateTableRender(val.length > 0);
    },
    columnOrderViews: {
      handler: async function (val) {
        await this.$nextTick();
        // nextTick here is to prevent a race condition(?), an error will occur without nextTick
        // if both tableData and columnOrderViews are provided when the table is rendered
        // then, in mounted, since tableData isn't blank, it queues up initTable to run on the next tick, expecting there to be data
        // but then this watcher removes the data before next tick arrives, which causes initTable's call to populateData to error out
        // simplest workaround is to wait for the next tick so that populateData can run first before removing the data

        let columnOrder;
        let columnOrderViewsWithDefault; // used to delay the emit for updating columnOrderViews until after both calls to updateTableRender have been executed

        if (val.length === 0) {
          columnOrder = [];
        } else if (this.appliedColumnOrderViewColumns) {
          // for some reason, the appliedColumnOrderViewColumns computed property already contains the updated value when this watcher is triggered, which is what we want
          columnOrder = this.appliedColumnOrderViewColumns;
        } else {
          columnOrderViewsWithDefault = this.identifyDefaultView(val);
          columnOrder = columnOrderViewsWithDefault.find((view) => view.Applied === 'Yes');
        }

        const tempTableData = this.privateTableData;
        this.privateTableData = [];
        this.updateTableRender(false);
        await this.$nextTick();
        // couldn't find a better way to do this other than clearing off the table and repopulating it
        // don't think this will have any adverse effects

        this.privateTableData = this.reorderColumns(tempTableData, columnOrder);
        this.updateTableRender(this.privateTableData.length > 0);

        if (columnOrderViewsWithDefault) {
          // this emit needs to be after both updateTableRender calls above have been executed
          // otherwise there will be a race condition(?) where two instances of the columnOrderViews watcher are running at once
          // and the second instance will clear off privateTableData while the first instance expects there to be data
          this.$emit('update:columnOrderViews', columnOrderViewsWithDefault);
        }
      },
      deep: true,
    },
    // startHeaderIndex: function(newval, oldval) {
    //   if (newval == null || oldval == null) {
    //     return;
    //   }

    //   let table = Array.from(this.scrollBlock.children).splice(1);
    //   table.forEach((t) => {
    //     let child = t.children[oldval];
    //     t.removeChild(child);
    //     t.insertBefore(child, t.children[newval]);
    //   });

    //   let col = this.columnArrange[oldval];
    //   this.columnArrange.splice(oldval, 1);
    //   this.columnArrange.splice(newval, 0, col);

    //   let width = this.colWidth[oldval];
    //   this.colWidth.splice(oldval, 1);
    //   this.colWidth.splice(newval, 0, width);
    //   this.placeResizer();
    // },
    selectedRow: function (val) {
      this.pSelectedObject = val;

      if (!this.scrollBlock) {
        return;
      }

      let allRows = Array.from(this.scrollBlock.children);
      allRows.forEach((a) => a.classList.remove(`${this.theme}-theme-row-selected`));

      if (val.length > 0) {
        let rowIDs = val.map((v) => v['_rowid']);

        let selected = allRows.filter((a) => rowIDs.includes(a['id']));
        selected.forEach((s) => s.classList.add(`${this.theme}-theme-row-selected`));
      }
    },
    selectedIndex: function (val) {
      if (this.privateTableData.length > 0) {
        let selected = [];
        val.forEach((v) => selected.push(this.privateTableData[v]));
        this.pSelectedObject = selected;

        let scrollBlock = this.$refs['scrollBlock'];
        let allRows = Array.from(scrollBlock.getElementsByClassName('table-row'));
        allRows.forEach((r) => r.classList.remove(`${this.theme}-theme-row-selected`));
        val.forEach((v) => allRows[v].classList.add(`${this.theme}-theme-row-selected`));
      }
    },
    hideRow: function () {
      // this.checkHiddenRows();
    },
    showContextMenu: function (val) {
      if (!val) {
        this.contextMenuItem = null;
        this.contextMenuList = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
$back-color: white;

.table-box-main {
  background-color: $back-color;
  height: 100%;
  width: 100%;
  position: relative;

  display: flex;
  flex-direction: column;

  &.fit-content-width {
    width: 100%;
    max-width: 100%;

    > .table-block > .table-header {
      min-width: 100%;

      > .table-column {
        flex: 0 0 auto;
      }
    }
  }

  > .top-block {
    display: flex;
    align-items: center;
    min-height: 40px;

    > .right-section {
      flex-grow: 1;
      display: flex;
      align-items: center;
      position: relative;
      z-index: 20;

      > .title {
        font-weight: bold;
        font-size: 1.2em;
        margin-right: 10px;
      }
    }

    > .left-section {
      text-align: right;
      line-height: 1.3;

      > .selected-indicator {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: flex-end;

        > .clear-arrow {
          border-color: black transparent transparent transparent;
          border-width: 5px 5px 0px 5px;
          border-style: solid;
          margin-left: 5px;
          cursor: pointer;
        }

        > .selected-button-block {
          position: absolute;
          background-color: white;
          width: 180px;
          top: 100%;
          right: 0;
          z-index: 110;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          border: 1px solid rgba(128, 128, 128, 0.5);
          border-radius: 3px;

          > button {
            margin: 3px;
            width: calc(100% - 6px);
          }
        }
      }
    }

    .last-refreshed{
      display: flex;
      align-items: center;
      gap: 6px;
      img{
        height: 12px;
        object-fit: contain;
      }
    }
  }

  > .table-block {
    overflow: auto;
    // border: 1px solid black;
    position: relative;
    //height: calc(100% - 80px);
    flex: 1;

    > .table-header {
      height: fit-content;
      width: fit-content;
      min-width: 100%;
      display: flex;
      position: sticky;
      top: 0;
      z-index: 11;

      > .table-column {
        padding: 5px;
        border-right: 1px solid white;
        // border-bottom: 1px solid black;
        overflow: visible;
        white-space: pre;
        text-overflow: ellipsis;
        background-color: $back-color;
        position: relative;

        > .title-block {
          text-align: center;
          display: flex;
          align-items: center;
          // font-weight: bold;
          cursor: pointer;
          user-select: none;

          > span {
            width: calc(100% - 15px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: pre;
          }
        }

        > input {
          width: 100%;
        }

        > .resizer {
          height: 100%;
          // background-color: rgba(0,0,255,0.3);
          width: 7px;
          position: absolute;
          right: -3.5px;
          top: 0;
          z-index: 15;
          cursor: ew-resize;
        }
      }
    }

    > .data-block {
      width: fit-content;
    }

    > .dragging-container,
    > .resizing-container {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      // background-color: rgba(200,0,0,0.3);
      z-index: 99;
    }

    > .dragging-container {
      cursor: grab;
    }
    > .resizing-container {
      cursor: ew-resize;
    }
  }

  > .bottom-block {
    display: flex;
    align-items: center;
    min-height: 40px;
    position: relative;

    > .bottom-content {
      display: flex;
      align-items: center;
      height: 100%;
      width: 100%;
      padding: 0 5px;
      position: absolute;
      left: 0;
      top: 0;
      overflow: hidden;

      > .bottom-spacer {
        width: 100%;
        height: 100%;
        min-width: 5px;
      }

      > .buttons-content {
        display: flex;
        align-items: center;
        width: fit-content;
        height: 100%;
        padding: 0 5px;

        > button,
        > div > button {
          margin-left: 5px;
          padding: 2px 5px;
          background: transparent;
          border: 1px solid white;
          color: white;
        }
      }
    }
  }

  > .no-items {
    width: 100%;
    text-align: center;
    // border: 1px solid black;
    background-color: rgba(150, 150, 150, 0.2);
    height: calc(100% - 40px);
    width: 100%;
  }

  > .context-menu-popup {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99;

    > .context-menu-background {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }

    > .context-menu-content {
      background-color: rgb(84, 84, 84);
      position: absolute;
      color: white;
      left: 0;
      top: 0;
      min-width: 200px;
      max-width: 200px;
      border-radius: 5px;

      > .context-menu-items {
        cursor: pointer;
        padding: 5px;

        &:hover {
          background-color: rgb(111, 111, 111);
        }
      }

      > :first-child {
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
      }

      > :last-child {
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }
  }

  > .ssr-loading {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    background-color: rgba(128, 128, 128, 0.3);
    z-index: 99;
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      background-color: white;
      border-radius: 5px;
      padding: 15px;

      &::after {
        content: ' . . .';
      }
    }
  }

  > .column-order-popup {
    height: calc(100% - #{$top-nav-height});
    width: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: $top-nav-height;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;

    > .popup-content {
      height: 95%;
      width: 70%;
      background-color: white;
      border-radius: 3px;
      padding: 5px;
      display: flex;
      flex-direction: row;

      > .call-to-action-buttons {
        margin-right: 3px;
        display: flex;
        flex-direction: column;

        > button {
          text-align: left;

          &:not(:first-child) {
            margin-top: 5px;
          }

          > img {
            height: 20px;
            width: 20px;
          }
        }
      }

      > .main-content {
        margin-left: 3px;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        > #view-details {
          max-height: calc(100% - 35px); // minus the height of the call to action buttons
          overflow-y: auto;

          > #view-general-details {
            display: flex;
            flex-direction: row;

            > #view-name-input {
              flex: 2;
            }

            > #user-owned-check-box {
              margin-left: 10px;
              flex: 1;
            }

            > #default-check-box {
              flex: 1;
            }
          }
        }

        .column-card {
          min-height: 35px;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          border: 1px solid black;
          border-radius: 5px;
          padding-left: 5px;
          cursor: pointer;

          &:not(:first-child) {
            margin-top: 5px;
          }

          &:is(.selected) {
            background-color: rgb(130, 130, 130);
            color: white;
          }

          &:hover {
            background-color: rgb(200, 200, 200);
          }

          &:hover:is(.selected) {
            background-color: rgb(160, 160, 160);
            color: white;
          }

          > .delete-column-icon {
            height: 20px;
            width: 20px;
          }
        }

        > .call-to-action-buttons {
          margin-top: 5px;
          display: flex;
          flex-direction: row;
          justify-content: flex-end;

          > button {
            text-align: left;

            &:not(:first-child) {
              margin-left: 5px;
            }

            > img {
              height: 20px;
              width: 20px;
            }
          }
        }
      }
    }
  }

  > .new-column-popup {
    overflow: visible;

    ::v-deep {
      // deep needed to target the content class inside the PopupV2 component
      .content {
        // NOT the content class for slot, but content class inside PopupV2 itself
        overflow: visible;
      }
    }
  }
}

.horizontal-line {
  height: 1px;
  width: 100%;
  background-color: black;
  position: sticky;
}
</style>

<style lang="scss">
.table-box-main.fit-content-width .table-row > .row-data {
  flex: 0 0 auto;
}

.table-row {
  display: flex;
  position: relative;
  width: fit-content;
  min-width: 100%;

  > .row-data {
    padding: 5px;
    border-right: 1px solid rgba(128, 128, 128, 0.5);
    // border-bottom: 1px solid black;
    display: flex;
    align-items: center;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    user-select: none;
  }

  > .row-overlay {
    position: absolute;
    left: 0;
    top: 0;
    width: calc(100% - 1px);
    height: calc(100% - 1px);
    max-width: 0;
    transition: 0.3s;
    z-index: 10;
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
  }

  > .row-overlay-error {
    background-color: red;
    color: white;
  }
}

.table-row:last-child {
  border-bottom: 1px solid rgba(128, 128, 128, 0.5);
}

.col-resizer {
  cursor: col-resize;
  z-index: 99;
  width: 10px;
  position: absolute;
  background-color: transparent;
  // background-color: rgba(0,0,0,0.3);

  // &:hover {
  //   background-color: green;
  // }
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  user-select: none;
  -webkit-user-select: none;
  background-color: lightgray;
  padding: 5px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.5);
  cursor: pointer;
  position: sticky;
  left: 0;

  > img {
    height: 14px;
    margin-right: 5px;
    transition: 0.3s;
  }
}

// main theme
.red-theme {
  background-color: rgb(255, 211, 211) !important;
}

.green-theme {
  background-color: rgb(195, 255, 195) !important;
}

.blue-theme {
  // background-color: rgb(205, 205, 255) !important;
  background-color: #535cda !important;
  color: white;
}
// main theme

// hover theme
.red-theme-row:hover {
  background-color: rgba(255, 0, 0, 0.5) !important;
  color: white;
}

.green-theme-row:hover {
  background-color: rgb(0, 128, 0) !important;
  color: white;
}

.blue-theme-row:hover {
  background-color: rgb(170, 170, 255) !important;

  > * {
    background-color: rgb(170, 170, 255) !important;
    // color: white;
  }
}
// hover theme

// even rows theme
.red-theme-row:nth-child(even) {
  background-color: rgb(255, 235, 235);
}

.green-theme-row:nth-child(even) {
  background-color: rgb(235, 255, 235);
}

.blue-theme-row:nth-child(even) {
  background-color: #e2e4ff;
}
// even rows theme

// buttons theme
.active-red-theme {
  background-color: rgba(255, 0, 0, 0.5) !important;
  color: white;
}

.active-green-theme {
  background-color: rgba(0, 128, 0, 0.5) !important;
  color: white;
}

.active-blue-theme {
  background-color: #a6acff !important;
  color: white;
}
// buttons theme

// row selected theme
.red-theme-row-selected {
  background-color: rgba(255, 0, 0, 0.5) !important;
  color: white;
}

.green-theme-row-selected {
  background-color: rgba(0, 128, 0, 0.5) !important;
  color: white;
}

.blue-theme-row-selected {
  background-color: rgb(110, 110, 255) !important;

  > *:not(button) {
    background-color: rgb(110, 110, 255) !important;
    color: white;
  }

  &:hover {
    > *:not(button) {
      background-color: rgb(140, 140, 255) !important;
      color: white;
    }
  }

  &:hover {
    background-color: rgb(140, 140, 255) !important;
  }
}
// row selected theme

//tooltip
.table-button-ref {
  position: relative;
}

.table-button-ref::after {
  content: attr(data-ref);
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 5px;
  text-align: left;

  background-color: rgb(84, 84, 84);
  color: white;
  padding: 5px 8px;
  border-radius: 5px;

  min-width: 120px;
  max-width: 200px;

  font-size: 12px;
  white-space: normal;
  z-index: 9999;

  opacity: 0;
  pointer-events: none;
  transform: translateY(-3px);
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.table-button-ref:hover::after {
  opacity: 1;
  transform: translateY(0);
}

</style>
